import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useTheme } from '@/contexts/ThemeContext';
import { ChatSessionProvider } from '@/contexts/ChatSessionContext';
import { Header, DataSourceFilter } from '@/components/shared';
import { CoachChat } from '@/components/coach-dashboard';
import { DocumentUpload } from '@/components/ai';
import { TabNavigation } from '@/components/shared';
import { getApiUrl } from '@/config/api';
import { useModals, useModalSuccess } from '@/contexts/ModalContext';
import { generateCommands } from '@/config/commands';
import { safeJsonParse } from '@/utils/apiUtils';
import { LayoutDashboard } from '@/utils/icons';
import { renderIcon } from '@/utils/iconMap';
import DashboardLayout from '@/components/shared/DashboardLayout';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import zoomPlugin from 'chartjs-plugin-zoom';
import Select, { components as selectComponents } from 'react-select';
import { AthletesTab, RankingsTab, AnalyticsDashboardTab, TeamOverviewTab, CoachOnboardingPanel } from '@/components/coach-dashboard';
import { ENABLED_PROVIDER_IDS, TERRA_PROVIDER_IDS, isAnyWearableConnected, getConnectedProviderIds } from '@/config/wearableProviders';
import { CHART_COLORS, getChartColorWithBackground, buildAthleteColorMap } from '@/utils/chartColors';
import { generateDateRange } from '@/utils/dateUtils';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  zoomPlugin
);

/**
 * Enhanced interface for comprehensive athlete data with all Whoop metrics
 */
interface AthleteData {
  athlete_id: number;
  athlete_name: string;
  whoop_connected: boolean;

  // Enhanced Recovery Data
  recovery_data?: Array<{
    date: string;
    recovery_score: number;
    hrv_rmssd_milli?: number;           // Added HRV data
    resting_heart_rate?: number;
    respiratory_rate?: number;          // Added respiratory data
    skin_temp_celsius?: number;         // Added skin temperature
    training_readiness?: string;
    created_at?: string;
  }>;

  // Enhanced Sleep Data with sleep architecture
  sleep_data?: Array<{
    date: string;
    total_duration_hours: number;
    sleep_quality_score?: number;           // Updated to match backend field name
    sleep_consistency_percentage?: number;    // Added consistency tracking
    sleep_efficiency?: number;               // Updated to match backend field name
    deep_sleep_hours?: number;
    light_sleep_hours?: number;              // Added light sleep tracking
    rem_sleep_hours?: number;                // Added REM sleep tracking
    awake_duration_hours?: number;           // Added wake time during sleep
    respiratory_rate?: number;               // Added sleep respiratory rate
    sleep_needed_baseline_hours?: number;    // Added recommended sleep
    disturbances?: number;                   // Added sleep interruptions
    is_nap?: boolean;                        // Added nap flag from backend
  }>;

  // Enhanced Activity Data with detailed strain metrics
  activity_data?: Array<{
    date: string;
    activity_type: string;
    strain_score?: number;
    duration_minutes: number;
    calories_burned?: number;
    average_heart_rate?: number;             // Added HR metrics
    max_heart_rate?: number;
    kilojoule?: number;                      // Added energy expenditure
    distance_meter?: number;                 // Added distance tracking
    altitude_gain_meter?: number;            // Added elevation data
    zone_duration?: {                        // Added HR zone data
      zone_1_minutes?: number;
      zone_2_minutes?: number;
      zone_3_minutes?: number;
      zone_4_minutes?: number;
      zone_5_minutes?: number;
    };
    perceived_exertion?: number;             // Added RPE data
    sport_id?: number;
  }>;

  // Enhanced statistics with comprehensive metrics
  stats?: {
    // Recovery stats
    average_recovery?: number;
    latest_recovery?: number;
    recovery_trend?: number;

    // HRV stats
    average_hrv?: number;
    latest_hrv?: number;
    hrv_trend?: number;

    // Sleep stats
    average_sleep_quality?: number;
    latest_sleep_quality?: number;
    average_sleep_duration?: number;
    average_sleep_efficiency?: number;
    average_deep_sleep?: number;
    average_rem_sleep?: number;

    // Strain/Activity stats
    average_strain?: number;
    latest_strain?: number;
    total_strain?: number;
    average_hr?: number;
    max_hr_peak?: number;

    // Data availability
    data_points?: number;
    last_sync?: string;
  };
}

/**
 * Interface for paginated API responses
 */
interface PaginatedResponse<T> {
  count: number;
  next?: string | null;
  previous?: string | null;
  results: T[];
}

/**
 * Interface for team data
 */
interface Team {
  id: number;
  name: string;
  organization: {
    id: number;
    name: string;
    description?: string;
  };
  // Present in API response; used client-side to filter to coach teams
  coaches?: Array<{ id: number; username: string; first_name?: string; last_name?: string; email?: string }>;
  athletes?: Array<{ id: number; username: string; first_name?: string; last_name?: string; email?: string }>;
  recovery_data?: {
    summary: {
      total_athletes: number;
      athletes_with_data: number;
      date_range_days: number;
    };
    athletes_data: AthleteData[];
  };
  sleep_data?: {
    summary: {
      total_athletes: number;
      athletes_with_data: number;
      date_range_days: number;
    };
    athletes_data: AthleteData[];
  };
  activity_data?: {
    summary: {
      total_athletes: number;
      athletes_with_data: number;
      date_range_days: number;
    };
    athletes_data: AthleteData[];
  };
  garmin_data?: {
    team_name: string;
    athletes_data: Array<{
      athlete_id: number;
      athlete_name: string;
      garmin_connected: boolean;
      whoop_connected: boolean;
      distance_data: Array<{ date: string; distance_miles: number }>;
      rhr_data: Array<{ date: string; resting_hr: number; whoop_rhr?: number; source?: string }>;
      whoop_rhr_data: Array<{ date: string; resting_hr: number; source: string }>;
      weekly_data: Array<{
        week_start_date: string;
        weekly_total: number;
        days_count: number;
        rolling_average?: number;
        weeks_in_window?: number;
      }>;
      sleep_efficiency_data?: Array<{
        date: string;
        efficiency: number;
        score?: number;
        source: string;
      }>;
      sleep_stages_data?: Array<{
        date: string;
        light_sleep_hours: number;
        deep_sleep_hours: number;
        rem_sleep_hours: number;
      }>;
      hr_zone_data?: Array<{
        date: string;
        zone_1_minutes: number;
        zone_2_minutes: number;
        zone_3_minutes: number;
        zone_4_minutes: number;
        zone_5_minutes: number;
        total_minutes: number;
      }>;
      stats: {
        average_distance_miles: number;
        total_distance_miles: number;
        average_rhr: number;
        latest_rhr: number;
        baseline_rhr: number | null;
        distance_data_points: number;
        rhr_data_points: number;
        average_sleep_efficiency?: number;
        sleep_efficiency_data_points?: number;
      };
    }>;
    team_weekly_stats?: Array<{
      week_start_date: string;
      team_mean: number;
      team_std_dev: number;
      team_upper_bound: number;
      team_lower_bound: number;
      athletes_count: number;
    }>;
    summary: {
      total_athletes: number;
      athletes_with_data: number;
      date_range_days: number;
    };
  };
  // Unified readiness data from ReadinessCalculatorV2 (single source of truth)
  readiness_data?: {
    generated_at: string;
    algorithm_version: string;
    team_name: string;
    summary: {
      total_athletes: number;
      optimal: number;
      moderate: number;
      low: number;
      athletes_with_data: number;
    };
    all_athletes: Array<{
      id: number;
      name: string;
      score: number;
      tier: string;
      data_source: string;
      whoop_connected: boolean;
      garmin_connected: boolean;
      coros_connected?: boolean;
      hrv: {
        value: number;
        trend: number;
        average: number;
        latest: number;
        consistency: number;
      };
      rhr: {
        value: number;
        trend: number;
      };
      sleep_trend: {
        value: number;
        trend: number;
      };
      recovery: {
        average: number;
        latest: number;
        trend: number;
        data_points: number;
      };
      sleep: {
        average_duration: number;
        latest_duration: number;
        average_quality: number;
        latest_quality: number;
        average_efficiency: number;
        data_points: number;
      };
      strain: {
        average: number;
        latest: number;
        total: number;
        data_points: number;
      };
      acwr?: {
        value: number | null;
        score: number | null;
        zone: string | null;
      } | null;
    }>;
    optimal_athletes: Array<any>;
    moderate_athletes: Array<any>;
    low_athletes: Array<any>;
  };
  error?: string;
}

/**
 * Interface for user information
 */
interface UserInfo {
  username: string;
  role?: string;
  full_name?: string;
  first_name?: string;
}

/**
 * Revamped Coach Dashboard with Recovery, Strain, and Sleep analytics.
 * Features comprehensive team insights with beautiful charts and ranking tables.
 */
const CoachDashboard: React.FC = () => {
  const [teamsData, setTeamsData] = useState<Team[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTeam, setSelectedTeam] = useState<Team | null>(null);
  const [days, setDays] = useState<number>(7);
  const [riskScoreMap, setRiskScoreMap] = useState<Map<number, { tier: string; risk_score: number }>>(new Map());
  const handleRiskMapReady = React.useCallback((map: Map<number, any>) => {
    const simplified = new Map<number, { tier: string; risk_score: number }>();
    for (const [id, data] of map) {
      simplified.set(id, { tier: data.tier, risk_score: data.risk_score });
    }
    setRiskScoreMap(simplified);
  }, []);
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [searchParams] = useSearchParams();
  const activeView = (searchParams.get('view') || 'overview') as 'overview' | 'analytics' | 'rankings' | 'athletes' | 'chat';


  // Auto-refresh state
  const [autoRefresh, setAutoRefresh] = useState<boolean>(true);
  const [refreshInterval, setRefreshInterval] = useState<number>(60000); // 60 seconds default
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
  const [garminLoading, setGarminLoading] = useState<boolean>(false);

  const navigate = useNavigate();
  const { isDark } = useTheme();
  const { openCreateUserModal, openInviteUserModal, openCreateTeamModal, openCreateOrgModal } = useModals();

  // Persisted dismissal of the zero-state onboarding panel, keyed per team so a
  // coach who dismisses it on Team A still sees it on a brand-new Team B.
  const [onboardingDismissedTeams, setOnboardingDismissedTeams] = useState<Set<number>>(() => {
    try {
      const raw = localStorage.getItem('coach_onboarding_dismissed_teams');
      return raw ? new Set(JSON.parse(raw)) : new Set();
    } catch {
      return new Set();
    }
  });
  const dismissOnboardingForTeam = (teamId: number) => {
    setOnboardingDismissedTeams(prev => {
      const next = new Set(prev);
      next.add(teamId);
      try {
        localStorage.setItem('coach_onboarding_dismissed_teams', JSON.stringify([...next]));
      } catch {
        /* localStorage unavailable */
      }
      return next;
    });
  };

  // Add state for selected athletes for each chart
  const [selectedRecoveryAthletes, setSelectedRecoveryAthletes] = useState<string[]>([]);
  const [selectedSleepAthletes, setSelectedSleepAthletes] = useState<string[]>([]);
  const [selectedStrainAthletes, setSelectedStrainAthletes] = useState<string[]>([]);

  // New filter states for HR Zones, Zone Trends, RHR, and Sleep Stages charts
  const [selectedHRZoneAthletes, setSelectedHRZoneAthletes] = useState<string[]>([]);
  const [selectedZoneTrendAthletes, setSelectedZoneTrendAthletes] = useState<string[]>([]);
  const [selectedRHRAthletes, setSelectedRHRAthletes] = useState<string[]>([]);
  const [selectedSleepStagesAthletes, setSelectedSleepStagesAthletes] = useState<string[]>([]);

  // Filter states for Daily Mileage and Weekly Mileage charts
  const [selectedDailyMileageAthletes, setSelectedDailyMileageAthletes] = useState<string[]>([]);
  const [selectedWeeklyMileageAthletes, setSelectedWeeklyMileageAthletes] = useState<string[]>([]);

  // Data source filtering state
  const [selectedDataSources, setSelectedDataSources] = useState<string[]>(ENABLED_PROVIDER_IDS);
  // Ref to always have the latest selectedDataSources for auto-refresh closures
  const selectedDataSourcesRef = useRef<string[]>(ENABLED_PROVIDER_IDS);
  useEffect(() => { selectedDataSourcesRef.current = selectedDataSources; }, [selectedDataSources]);

  // Fetch cancellation: abort in-flight requests when a new fetch starts or component unmounts
  const fetchControllerRef = useRef<AbortController | null>(null);
  const fetchGenerationRef = useRef(0);
  const fetchInFlightRef = useRef(false);

  // Derive which providers are connected by any athlete on the team
  const teamConnectedSources = useMemo(() => {
    if (!selectedTeam?.readiness_data?.all_athletes) return ENABLED_PROVIDER_IDS;
    const connected = new Set<string>();
    for (const athlete of selectedTeam.readiness_data.all_athletes) {
      for (const id of getConnectedProviderIds(athlete)) {
        connected.add(id);
      }
    }
    return connected.size > 0 ? Array.from(connected) : ENABLED_PROVIDER_IDS;
  }, [selectedTeam?.readiness_data?.all_athletes]);

  // Stable color map: every athlete gets the same color across ALL charts
  const athleteColorMap = useMemo(() => {
    if (!selectedTeam) return new Map<string, string>();
    const allNames: string[] = [];
    selectedTeam.recovery_data?.athletes_data?.forEach(a => allNames.push(a.athlete_name));
    selectedTeam.sleep_data?.athletes_data?.forEach(a => allNames.push(a.athlete_name));
    selectedTeam.activity_data?.athletes_data?.forEach(a => allNames.push(a.athlete_name));
    selectedTeam.garmin_data?.athletes_data?.forEach(a => allNames.push(a.athlete_name));
    return buildAthleteColorMap(allNames);
  }, [selectedTeam?.recovery_data, selectedTeam?.sleep_data, selectedTeam?.activity_data, selectedTeam?.garmin_data]);

  // Keep selectedDataSources in sync — remove any providers that are no longer connected
  useEffect(() => {
    const filtered = selectedDataSources.filter((s: string) => teamConnectedSources.includes(s));
    const next = filtered.length > 0 ? filtered : teamConnectedSources;
    // Use set comparison to avoid spurious refetches from ordering differences
    const currentSet = new Set(selectedDataSources);
    const nextSet = new Set(next);
    const setsEqual = currentSet.size === nextSet.size && [...currentSet].every(s => nextSet.has(s));
    if (!setsEqual) {
      setSelectedDataSources(next);
      // Refetch with corrected sources so data matches the trimmed selection
      fetchTeamsData(true, next);
    }
  }, [teamConnectedSources]);

  // Add state for focused athletes (clicked on graph)
  const [focusedRecoveryAthlete, setFocusedRecoveryAthlete] = useState<string | null>(null);
  const [focusedSleepAthlete, setFocusedSleepAthlete] = useState<string | null>(null);
  const [focusedStrainAthlete, setFocusedStrainAthlete] = useState<string | null>(null);

  // Focused athlete states for new filters
  const [focusedHRZoneAthlete, setFocusedHRZoneAthlete] = useState<string | null>(null);
  const [focusedZoneTrendAthlete, setFocusedZoneTrendAthlete] = useState<string | null>(null);
  const [focusedRHRAthlete, setFocusedRHRAthlete] = useState<string | null>(null);
  const [focusedSleepStagesAthlete, setFocusedSleepStagesAthlete] = useState<string | null>(null);
  const [focusedDailyMileageAthlete, setFocusedDailyMileageAthlete] = useState<string | null>(null);
  const [focusedWeeklyMileageAthlete, setFocusedWeeklyMileageAthlete] = useState<string | null>(null);

  // Reset focused athlete if they're unselected from dropdown
  useEffect(() => {
    if (focusedRecoveryAthlete && !selectedRecoveryAthletes.includes(focusedRecoveryAthlete)) {
      setFocusedRecoveryAthlete(null);
    }
  }, [selectedRecoveryAthletes, focusedRecoveryAthlete]);

  useEffect(() => {
    if (focusedSleepAthlete && !selectedSleepAthletes.includes(focusedSleepAthlete)) {
      setFocusedSleepAthlete(null);
    }
  }, [selectedSleepAthletes, focusedSleepAthlete]);

  useEffect(() => {
    if (focusedStrainAthlete && !selectedStrainAthletes.includes(focusedStrainAthlete)) {
      setFocusedStrainAthlete(null);
    }
  }, [selectedStrainAthletes, focusedStrainAthlete]);

  // Reset focused athletes for new filters when deselected
  useEffect(() => {
    if (focusedHRZoneAthlete && !selectedHRZoneAthletes.includes(focusedHRZoneAthlete)) {
      setFocusedHRZoneAthlete(null);
    }
  }, [selectedHRZoneAthletes, focusedHRZoneAthlete]);

  useEffect(() => {
    if (focusedZoneTrendAthlete && !selectedZoneTrendAthletes.includes(focusedZoneTrendAthlete)) {
      setFocusedZoneTrendAthlete(null);
    }
  }, [selectedZoneTrendAthletes, focusedZoneTrendAthlete]);

  useEffect(() => {
    if (focusedRHRAthlete && !selectedRHRAthletes.includes(focusedRHRAthlete)) {
      setFocusedRHRAthlete(null);
    }
  }, [selectedRHRAthletes, focusedRHRAthlete]);

  useEffect(() => {
    if (focusedSleepStagesAthlete && !selectedSleepStagesAthletes.includes(focusedSleepStagesAthlete)) {
      setFocusedSleepStagesAthlete(null);
    }
  }, [selectedSleepStagesAthletes, focusedSleepStagesAthlete]);

  useEffect(() => {
    if (focusedDailyMileageAthlete && !selectedDailyMileageAthletes.includes(focusedDailyMileageAthlete)) {
      setFocusedDailyMileageAthlete(null);
    }
  }, [selectedDailyMileageAthletes, focusedDailyMileageAthlete]);

  useEffect(() => {
    if (focusedWeeklyMileageAthlete && !selectedWeeklyMileageAthletes.includes(focusedWeeklyMileageAthlete)) {
      setFocusedWeeklyMileageAthlete(null);
    }
  }, [selectedWeeklyMileageAthletes, focusedWeeklyMileageAthlete]);

  // Add menu open state for each dropdown
  const [recoveryMenuOpen, setRecoveryMenuOpen] = useState(false);
  const [sleepMenuOpen, setSleepMenuOpen] = useState(false);
  const [strainMenuOpen, setStrainMenuOpen] = useState(false);

  // Menu open states for new filters
  const [hrZoneMenuOpen, setHRZoneMenuOpen] = useState(false);
  const [zoneTrendMenuOpen, setZoneTrendMenuOpen] = useState(false);
  const [rhrMenuOpen, setRHRMenuOpen] = useState(false);
  const [sleepStagesMenuOpen, setSleepStagesMenuOpen] = useState(false);
  const [dailyMileageMenuOpen, setDailyMileageMenuOpen] = useState(false);
  const [weeklyMileageMenuOpen, setWeeklyMileageMenuOpen] = useState(false);

  // Top control menus (Team, Period, Athletes)
  const [teamMenuOpen, setTeamMenuOpen] = useState(false);
  const [periodMenuOpen, setPeriodMenuOpen] = useState(false);
  const [athletesMenuOpen, setAthletesMenuOpen] = useState(false);

  // Refs for outside-click detection on top controls
  const teamRef = React.useRef<HTMLDivElement | null>(null);
  const periodRef = React.useRef<HTMLDivElement | null>(null);
  const athletesRef = React.useRef<HTMLDivElement | null>(null);

  // Add state to track hover for the Select control
  const [recoveryControlHovered, setRecoveryControlHovered] = useState(false);
  const [sleepControlHovered, setSleepControlHovered] = useState(false);
  const [strainControlHovered, setStrainControlHovered] = useState(false);

  /**
   * Data validation functions to exclude non-wearing periods and invalid data
   */








  /**
   * Effect to initialize dashboard data
   */
  useEffect(() => {
    // Check if user is authenticated
    const token = localStorage.getItem('access');
    if (!token) {
      navigate('/login');
      return;
    }

    // Get user info from localStorage
    const userInfoString = localStorage.getItem('user_info');
    if (userInfoString) {
      try {
        const user: UserInfo = JSON.parse(userInfoString);
        setUserInfo(user);
      } catch (err) {
        console.error('Error parsing user info:', err);
      }
    }

    // Refresh user info from backend to ensure latest connection status
    refreshUserInfo();

    fetchTeamsData();

    // Cancel in-flight fetches on unmount (prevents stale callbacks after navigation)
    return () => {
      fetchControllerRef.current?.abort();
    };
  }, [navigate]);

  /**
   * Listen for team visibility changes (coach toggled "show my data" in Settings)
   * and force-refresh so the change appears immediately.
   */
  useEffect(() => {
    const onVisibilityChange = () => fetchTeamsData(true, selectedDataSourcesRef.current, true);
    window.addEventListener('team-visibility-changed', onVisibilityChange);
    return () => window.removeEventListener('team-visibility-changed', onVisibilityChange);
  }, [days]);

  /**
   * Auto-refresh effect - polls for new data at regular intervals
   */
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      // Skip auto-refresh if a fetch is already in-flight (user action or previous refresh)
      if (fetchInFlightRef.current) return;
      fetchTeamsData(true, selectedDataSourcesRef.current);
    }, refreshInterval);

    return () => clearInterval(interval);
  }, [autoRefresh, refreshInterval, days]); // Re-create interval if settings change

  // Close top control dropdowns on outside click (including clicking the card)
  useEffect(() => {
    const isInsideAnyMenu = (target: Node | null): boolean => {
      if (!target) return false;
      if (!(target instanceof Element)) return false;
      return !!target.closest('.react-select__menu');
    };

    const handleOutside = (e: MouseEvent | TouchEvent) => {
      const target = e.target as Node | null;
      if (isInsideAnyMenu(target)) return;

      if (teamMenuOpen && teamRef.current && !teamRef.current.contains(target as Node)) setTeamMenuOpen(false);
      if (periodMenuOpen && periodRef.current && !periodRef.current.contains(target as Node)) setPeriodMenuOpen(false);
      if (athletesMenuOpen && athletesRef.current && !athletesRef.current.contains(target as Node)) setAthletesMenuOpen(false);
    };

    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('touchstart', handleOutside, { passive: true });
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('touchstart', handleOutside);
    };
  }, [teamMenuOpen, periodMenuOpen, athletesMenuOpen]);

  /**
   * Effect to refetch data when days change, preserving selected team
   */
  useEffect(() => {
    if (teamsData.length > 0) {
      fetchTeamsData();
    }
  }, [days]);

  // Preserve athlete selections across period changes; reset only when team changes
  const prevTeamIdRef = React.useRef<number | null>(null);
  // Track whether garmin_data was available when we last processed this team
  const prevTeamHadGarminRef = React.useRef(false);
  // Track teams whose Garmin-specific filters have already been initialized.
  // Prevents Phase 1/Phase 2 refresh cycles from wiping the user's chosen filter
  // selections every time garmin_data temporarily disappears and re-arrives.
  const garminInitializedTeamIdsRef = React.useRef<Set<number>>(new Set());
  // Track previous data sources to detect when Garmin is re-enabled
  const prevDataSourcesRef = React.useRef<string[]>(selectedDataSources);
  // Skip pruning when selectedTeam update is caused by a data source toggle
  const isSourceToggleRef = React.useRef(false);
  useEffect(() => {
    if (!selectedTeam) return;
    const availableNames = selectedTeam.recovery_data?.athletes_data.map(a => a.athlete_name) || [];

    // Get athletes with HR zone data (Garmin)
    const hrZoneAthletes = selectedTeam.garmin_data?.athletes_data
      ?.filter(a => a.hr_zone_data && a.hr_zone_data.length > 0)
      .map(a => a.athlete_name) || [];

    // Get athletes with sleep efficiency data (combined Garmin + WHOOP)
    const sleepEfficiencyAthletes = (() => {
      const athleteNames = new Set<string>();
      // Garmin athletes with sleep_efficiency_data
      selectedTeam.garmin_data?.athletes_data
        ?.filter(a => a.sleep_efficiency_data && a.sleep_efficiency_data.length > 0)
        .forEach(a => athleteNames.add(a.athlete_name));
      // WHOOP athletes with sleep_efficiency in sleep_data
      selectedTeam.sleep_data?.athletes_data
        ?.filter(a => a.sleep_data?.some(s => s.sleep_efficiency !== undefined))
        .forEach(a => athleteNames.add(a.athlete_name));
      return Array.from(athleteNames);
    })();

    // Get athletes with sleep stages data (combined WHOOP + Garmin)
    const sleepStagesAthletes = (() => {
      const athleteNames = new Set<string>();
      // WHOOP athletes with sleep stage data (check existence, not > 0, to include 0 values)
      selectedTeam.sleep_data?.athletes_data
        ?.filter(a => a.sleep_data?.some(s =>
          (s.light_sleep_hours != null) ||
          (s.deep_sleep_hours != null) ||
          (s.rem_sleep_hours != null)))
        .forEach(a => athleteNames.add(a.athlete_name));
      // Garmin athletes with sleep_stages_data
      selectedTeam.garmin_data?.athletes_data
        ?.filter(a => a.sleep_stages_data && a.sleep_stages_data.length > 0)
        .forEach(a => athleteNames.add(a.athlete_name));
      return Array.from(athleteNames);
    })();

    // Get athletes with mileage data
    const mileageAthletes = selectedTeam.garmin_data?.athletes_data
      ?.filter(a => a.distance_data && a.distance_data.length > 0)
      .map(a => a.athlete_name) || [];

    // Get athletes with weekly mileage data
    const weeklyMileageAthletes = selectedTeam.garmin_data?.athletes_data
      ?.filter(a => a.weekly_data && a.weekly_data.length > 0)
      .map(a => a.athlete_name) || [];

    const garminJustArrived = !prevTeamHadGarminRef.current && !!selectedTeam.garmin_data;

    if (prevTeamIdRef.current !== selectedTeam.id) {
      // Team changed: initialize all selections to all available athletes
      setSelectedRecoveryAthletes(availableNames);
      setSelectedSleepAthletes(availableNames);
      setSelectedStrainAthletes(availableNames);

      // Initialize new filter selections
      setSelectedHRZoneAthletes(hrZoneAthletes);
      setSelectedZoneTrendAthletes(hrZoneAthletes);
      setSelectedRHRAthletes(sleepEfficiencyAthletes);
      setSelectedSleepStagesAthletes(sleepStagesAthletes);
      setSelectedDailyMileageAthletes(mileageAthletes);
      setSelectedWeeklyMileageAthletes(weeklyMileageAthletes);

      // Reset Garmin-init tracking for the new team; mark initialized now if
      // Garmin data was already present in the first response.
      garminInitializedTeamIdsRef.current.delete(selectedTeam.id);
      if (selectedTeam.garmin_data) {
        garminInitializedTeamIdsRef.current.add(selectedTeam.id);
      }
    } else if (isSourceToggleRef.current) {
      // Data source toggle: skip pruning — the data-source-specific effect handles re-init
      isSourceToggleRef.current = false;
    } else if (garminJustArrived) {
      // Phase 2 Garmin data arrived. Only auto-populate selections the *first*
      // time Garmin data lands for this team; on subsequent refresh cycles
      // (Phase 1 temporarily drops garmin_data), preserve the user's choices.
      if (!garminInitializedTeamIdsRef.current.has(selectedTeam.id)) {
        setSelectedHRZoneAthletes(hrZoneAthletes);
        setSelectedZoneTrendAthletes(hrZoneAthletes);
        setSelectedRHRAthletes(sleepEfficiencyAthletes);
        setSelectedSleepStagesAthletes(sleepStagesAthletes);
        setSelectedDailyMileageAthletes(mileageAthletes);
        setSelectedWeeklyMileageAthletes(weeklyMileageAthletes);
        garminInitializedTeamIdsRef.current.add(selectedTeam.id);
      } else {
        // Preserve user selections, just drop any athlete names no longer available.
        setSelectedHRZoneAthletes(prev => prev.filter(name => hrZoneAthletes.includes(name)));
        setSelectedZoneTrendAthletes(prev => prev.filter(name => hrZoneAthletes.includes(name)));
        setSelectedRHRAthletes(prev => prev.filter(name => sleepEfficiencyAthletes.includes(name)));
        setSelectedSleepStagesAthletes(prev => prev.filter(name => sleepStagesAthletes.includes(name)));
        setSelectedDailyMileageAthletes(prev => prev.filter(name => mileageAthletes.includes(name)));
        setSelectedWeeklyMileageAthletes(prev => prev.filter(name => weeklyMileageAthletes.includes(name)));
      }
    } else {
      // Same team (e.g., period change): prune selections to available names, keep user choices
      setSelectedRecoveryAthletes(prev => prev.filter(name => availableNames.includes(name)));
      setSelectedSleepAthletes(prev => prev.filter(name => availableNames.includes(name)));
      setSelectedStrainAthletes(prev => prev.filter(name => availableNames.includes(name)));

      // Prune new filter selections — if pruning empties the list but athletes are
      // available, re-initialize to all (handles Phase 2 Garmin data arriving late).
      // Only reset if prev was non-empty to preserve user's explicit "Deselect All" choice.
      const pruneOrReset = (prev: string[], available: string[]): string[] => {
        const filtered = prev.filter(name => available.includes(name));
        return prev.length > 0 && filtered.length === 0 && available.length > 0 ? available : filtered;
      };

      // Skip Garmin-dependent prunes during Phase 1 (garmin_data temporarily
      // absent). Pruning against an empty `available` list would wipe the
      // user's selection; Phase 2's garminJustArrived branch will prune
      // correctly against the real athlete list a moment later.
      if (selectedTeam.garmin_data) {
        setSelectedHRZoneAthletes(prev => pruneOrReset(prev, hrZoneAthletes));
        setSelectedZoneTrendAthletes(prev => pruneOrReset(prev, hrZoneAthletes));
        setSelectedRHRAthletes(prev => pruneOrReset(prev, sleepEfficiencyAthletes));
        setSelectedSleepStagesAthletes(prev => pruneOrReset(prev, sleepStagesAthletes));
        setSelectedDailyMileageAthletes(prev => pruneOrReset(prev, mileageAthletes));
        setSelectedWeeklyMileageAthletes(prev => pruneOrReset(prev, weeklyMileageAthletes));
      }
    }

    prevTeamIdRef.current = selectedTeam.id;
    prevTeamHadGarminRef.current = !!selectedTeam.garmin_data;
  }, [selectedTeam]);

  // Re-initialize data-source-specific athlete selections when sources change
  useEffect(() => {
    if (!selectedTeam) return;

    // Check previous states BEFORE updating the ref
    const terraWasOff = !TERRA_PROVIDER_IDS.some(id => prevDataSourcesRef.current.includes(id));
    const terraIsNowOn = TERRA_PROVIDER_IDS.some(id => selectedDataSources.includes(id));
    const whoopWasOff = !prevDataSourcesRef.current.includes('whoop');
    const whoopIsNowOn = selectedDataSources.includes('whoop');

    // Re-initialize Terra-specific selections when any Terra provider is re-enabled
    if (terraWasOff && terraIsNowOn && selectedTeam.garmin_data) {
      const hrZoneAthletes = selectedTeam.garmin_data.athletes_data
        ?.filter(a => a.hr_zone_data && a.hr_zone_data.length > 0)
        .map(a => a.athlete_name) || [];

      const mileageAthletes = selectedTeam.garmin_data.athletes_data
        ?.filter(a => a.distance_data && a.distance_data.length > 0)
        .map(a => a.athlete_name) || [];

      const weeklyMileageAthletes = selectedTeam.garmin_data.athletes_data
        ?.filter(a => a.weekly_data && a.weekly_data.length > 0)
        .map(a => a.athlete_name) || [];

      const sleepEfficiencyAthletes = selectedTeam.garmin_data.athletes_data
        ?.filter(a => a.sleep_efficiency_data && a.sleep_efficiency_data.length > 0)
        .map(a => a.athlete_name) || [];

      // Re-initialize all Garmin-specific chart filters
      setSelectedHRZoneAthletes(hrZoneAthletes);
      setSelectedZoneTrendAthletes(hrZoneAthletes);
      setSelectedDailyMileageAthletes(mileageAthletes);
      setSelectedWeeklyMileageAthletes(weeklyMileageAthletes);

      // Sleep Efficiency is a combined source chart - re-add Garmin athletes
      setSelectedRHRAthletes(prev => {
        const combined = new Set([...prev, ...sleepEfficiencyAthletes]);
        return Array.from(combined);
      });

      // Sleep Stages is a combined source chart - re-add Garmin athletes
      const garminSleepStagesAthletes = selectedTeam.garmin_data.athletes_data
        ?.filter(a => a.sleep_stages_data && a.sleep_stages_data.length > 0)
        .map(a => a.athlete_name) || [];

      setSelectedSleepStagesAthletes(prev => {
        const combined = new Set([...prev, ...garminSleepStagesAthletes]);
        return Array.from(combined);
      });
    }

    // Re-initialize WHOOP-specific selections when WHOOP is re-enabled
    if (whoopWasOff && whoopIsNowOn && selectedTeam.sleep_data) {
      // WHOOP athletes with sleep efficiency
      const whoopSleepEfficiencyAthletes = selectedTeam.sleep_data.athletes_data
        ?.filter(a => a.sleep_data?.some(s => s.sleep_efficiency !== undefined))
        .map(a => a.athlete_name) || [];

      // WHOOP athletes with sleep stage data (check existence, not > 0, to include 0 values)
      const whoopSleepStagesAthletes = selectedTeam.sleep_data.athletes_data
        ?.filter(a => a.sleep_data?.some(s =>
          (s.light_sleep_hours != null) ||
          (s.deep_sleep_hours != null) ||
          (s.rem_sleep_hours != null)))
        .map(a => a.athlete_name) || [];

      // Sleep Efficiency - re-add WHOOP athletes
      setSelectedRHRAthletes(prev => {
        const combined = new Set([...prev, ...whoopSleepEfficiencyAthletes]);
        return Array.from(combined);
      });

      // Sleep Stages - re-add WHOOP athletes
      setSelectedSleepStagesAthletes(prev => {
        const combined = new Set([...prev, ...whoopSleepStagesAthletes]);
        return Array.from(combined);
      });
    }

    // Update ref AFTER all checks have run
    prevDataSourcesRef.current = selectedDataSources;
  }, [selectedDataSources, selectedTeam]);

  /**
   * Fetch comprehensive team data including recovery, sleep, and activity
   * @param silent - If true, shows refresh indicator instead of full loading state
   * @param sourcesOverride - Optional override for selectedDataSources to avoid stale closure
   */
  const fetchTeamsData = async (silent: boolean = false, sourcesOverride?: string[], forceRefresh: boolean = false): Promise<void> => {
    // Cancel any in-flight fetch (previous navigation, stale auto-refresh, etc.)
    fetchControllerRef.current?.abort();
    const controller = new AbortController();
    fetchControllerRef.current = controller;
    const generation = ++fetchGenerationRef.current;
    fetchInFlightRef.current = true;

    const token = localStorage.getItem('access');
    // Use override if provided, otherwise fall back to state (which may be stale in callbacks)
    const effectiveSources = sourcesOverride ?? selectedDataSources;
    // On silent (auto-refresh), use cache-busting to get fresh data; on initial load, allow cache
    const cacheBust = silent;
    try {
      // Only show full loading on initial load, use silent refresh for background updates
      if (!silent) {
        setLoading(true);
      } else {
        setIsRefreshing(true);
      }

      // First, get all teams for the current user
      const teamsUrl = cacheBust
        ? getApiUrl(`/api/teams/teams/?_t=${Date.now()}`)
        : getApiUrl('/api/teams/teams/');
      const teamsHeaders: Record<string, string> = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      };
      if (cacheBust) teamsHeaders['Cache-Control'] = 'no-cache';

      const teamsResponse = await fetch(teamsUrl, { headers: teamsHeaders, signal: controller.signal });

      if (!teamsResponse.ok) {
        if (teamsResponse.status === 401) {
          localStorage.removeItem('access');
          localStorage.removeItem('refresh');
          navigate('/login');
          return;
        }
        throw new Error(`Error fetching teams: ${teamsResponse.status}`);
      }

      const teamsApiResponse: any = await teamsResponse.json();

      // Handle different response formats:
      // 1. Direct array: [team1, team2, ...]
      // 2. Paginated response: { results: [...], count: X, next: ..., previous: ... }
      // 3. Data wrapper: { data: [team1, team2, ...] }
      const teams: Team[] = Array.isArray(teamsApiResponse)
        ? teamsApiResponse
        : (teamsApiResponse.results || teamsApiResponse.data || []);

      // Validate that teams is an array
      if (!Array.isArray(teams)) {
        console.error('Teams API response is not in expected format:', teamsApiResponse);
        throw new Error('Invalid teams data format received from API');
      }

      if (teams.length === 0) {
        console.warn('CoachDashboard: No teams returned from API. This could mean:');
        console.warn('  1. User has no teams assigned');
        console.warn('  2. User role does not have access to teams');
        console.warn('  3. API endpoint returned empty results');
        console.warn('  API Response:', teamsApiResponse);
      }

      // Build common headers (cache-busting only on explicit refresh)
      const dataHeaders: Record<string, string> = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      };
      if (cacheBust) dataHeaders['Cache-Control'] = 'no-cache';
      const ts = cacheBust ? `&_t=${Date.now()}` : '';

      // --- PHASE 1: Fetch fast endpoints (recovery, sleep, activity, readiness) ---
      // These are fast (<1s each). Render the dashboard as soon as these return.
      const teamsWithData = await Promise.all(teams.map(async (team) => {
        try {
          const sourceParam = effectiveSources.join(',');

          const [recoveryResponse, sleepResponse, activityResponse, readinessResponse] = await Promise.all([
            fetch(getApiUrl(`/api/teams/teams/${team.id}/athletes-recovery/?days=${days}&source=${sourceParam}${ts}`), {
              headers: dataHeaders, signal: controller.signal,
            }),
            fetch(getApiUrl(`/api/teams/teams/${team.id}/athletes-sleep/?days=${days}&source=${sourceParam}${ts}`), {
              headers: dataHeaders, signal: controller.signal,
            }),
            fetch(getApiUrl(`/api/teams/teams/${team.id}/athletes-activity/?days=${days}&source=${sourceParam}${ts}`), {
              headers: dataHeaders, signal: controller.signal,
            }),
            fetch(getApiUrl(`/api/teams/teams/${team.id}/readiness/?days=${days}${forceRefresh ? '&refresh=true' : ''}${ts}`), {
              headers: dataHeaders, signal: controller.signal,
            })
          ]);

          let updatedTeam = { ...team };

          if (recoveryResponse.ok) {
            const recoveryData = await recoveryResponse.json();
            updatedTeam.recovery_data = recoveryData;
          }

          if (sleepResponse.ok) {
            const sleepData = await sleepResponse.json();
            updatedTeam.sleep_data = sleepData;
          }

          if (activityResponse.ok) {
            const activityData = await activityResponse.json();
            updatedTeam.activity_data = activityData;
          }

          // Store unified readiness data (single source of truth)
          if (readinessResponse.ok) {
            const readinessData = await readinessResponse.json();
            updatedTeam.readiness_data = readinessData;
          }

          return updatedTeam;
        } catch (err) {
          // Re-throw AbortError so the outer catch handles it (prevents console spam)
          if (err instanceof DOMException && err.name === 'AbortError') throw err;
          console.error(`Error fetching data for team ${team.id}:`, err);
          return { ...team, error: err instanceof Error ? err.message : 'Failed to fetch team data' };
        }
      }));

      // Bail if a newer fetch has started (this one is stale)
      if (generation !== fetchGenerationRef.current) return;

      setTeamsData(prev => {
        // When a create action triggered this fetch, auto-select the newly added team
        if (forceRefresh && prev.length > 0) {
          const oldIds = new Set(prev.map(t => t.id));
          const newTeam = teamsWithData.find(t => !oldIds.has(t.id));
          if (newTeam) {
            setSelectedTeam(newTeam);
            return teamsWithData;
          }
        }

        if (teamsWithData.length > 0) {
          if (selectedTeam) {
            const currentTeam = teamsWithData.find(team => team.id === selectedTeam.id);
            setSelectedTeam(currentTeam ?? teamsWithData[0]);
          } else {
            setSelectedTeam(teamsWithData[0]);
          }
        }

        return teamsWithData;
      });

      // Release the loading spinner — dashboard is now interactive
      setLoading(false);
      setIsRefreshing(false);
      setLastRefresh(new Date());

      // --- PHASE 2: Fetch Garmin data in background (slow endpoint) ---
      // This runs after the dashboard is already rendered and interactive.
      setGarminLoading(true);
      Promise.all(teamsWithData.map(async (team) => {
        try {
          const sourceParam = effectiveSources.join(',');
          const garminResponse = await fetch(
            getApiUrl(`/api/teams/teams/${team.id}/athletes-garmin/?days=${days}&source=${sourceParam}${ts}`),
            { headers: dataHeaders, signal: controller.signal }
          );
          if (garminResponse.ok) {
            const garminData = await garminResponse.json();
            return { teamId: team.id, garminData };
          }
          return null;
        } catch (err) {
          if (err instanceof DOMException && err.name === 'AbortError') return null;
          console.error(`Error fetching Garmin data for team ${team.id}:`, err);
          return null;
        }
      })).then((garminResults) => {
        // Bail if a newer fetch has started (this garmin result is stale)
        if (generation !== fetchGenerationRef.current) return;
        // Merge garmin_data into existing teams state
        setTeamsData(prevTeams => prevTeams.map(team => {
          const garminResult = garminResults.find(r => r && r.teamId === team.id);
          if (garminResult) {
            return { ...team, garmin_data: garminResult.garminData };
          }
          return team;
        }));
        // Also update selectedTeam if it received garmin data
        setSelectedTeam(prev => {
          if (!prev) return prev;
          const garminResult = garminResults.find(r => r && r.teamId === prev.id);
          if (garminResult) {
            return { ...prev, garmin_data: garminResult.garminData };
          }
          return prev;
        });
        setGarminLoading(false);
        // Clear in-flight flag after Phase 2 completes (not after Phase 1)
        if (generation === fetchGenerationRef.current) {
          fetchInFlightRef.current = false;
        }
      }).catch(() => {
        setGarminLoading(false);
        if (generation === fetchGenerationRef.current) {
          fetchInFlightRef.current = false;
        }
      });
    } catch (err) {
      // Silently ignore aborted fetches (navigation away, newer fetch started)
      if (err instanceof DOMException && err.name === 'AbortError') {
        // Clear in-flight flag if this was the active fetch
        if (generation === fetchGenerationRef.current) {
          fetchInFlightRef.current = false;
        }
        return;
      }
      // Skip state updates if a newer fetch has started
      if (generation !== fetchGenerationRef.current) return;

      console.error('CoachDashboard Error in fetchTeamsData:', err);
      const errorMessage = err instanceof Error ? err.message : 'An error occurred while fetching team data';
      console.error('CoachDashboard Error details:', errorMessage);
      setError(errorMessage);
      setTeamsData([]); // Ensure teams data is cleared on error
      setLoading(false);
      setIsRefreshing(false);
      setLastRefresh(new Date());
      fetchInFlightRef.current = false;
    }
  };

  useModalSuccess(() => fetchTeamsData(true, undefined, true));

  /**
   * Refresh user info from backend to get updated connection status
   */
  const refreshUserInfo = async (): Promise<void> => {
    try {
      const token = localStorage.getItem('access');
      if (!token) return;

      const response = await fetch(getApiUrl('/api/users/me/'), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const userData = await response.json();
        localStorage.setItem('user_info', JSON.stringify(userData));
        setUserInfo(userData);
      }
    } catch (err) {
      console.error('CoachDashboard: Error refreshing user info:', err);
    }
  };

  /**
   * Manual refresh handler - Triggers immediate Celery sync
   */
  const handleManualRefresh = async () => {
    setIsRefreshing(true);

    try {
      const token = localStorage.getItem('access');
      if (!token) {
        navigate('/login');
        return;
      }

      // Trigger Celery bulk sync for all athletes
      const response = await fetch(getApiUrl('/api/wearables/sync-all/'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const result = await safeJsonParse(response, 'triggering manual sync');

      if (!result.success) {
        throw new Error(result.error || 'Failed to trigger sync');
      }

      // Show success message
      alert('Data sync initiated! Your dashboard will refresh in a few seconds...');

      // Wait 8 seconds for sync to complete, then refresh
      await new Promise(resolve => setTimeout(resolve, 8000));

      // Refetch all data
      await fetchTeamsData(true);

    } catch (error) {
      console.error('Error triggering manual refresh:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      alert(`Failed to sync data: ${errorMessage}`);
    } finally {
      setIsRefreshing(false);
    }
  };

  const commands = useMemo(() => generateCommands({
    navigate,
    openCreateUserModal,
    openInviteUserModal,
    openCreateTeamModal,
    openCreateOrgModal,
    setDays,
    fetchTeamsData,
  }), [navigate, openCreateUserModal, openInviteUserModal, openCreateTeamModal, openCreateOrgModal]);

  /**
   * Handle user logout
   */
  const handleLogout = (): void => {
    localStorage.removeItem('access');
    localStorage.removeItem('refresh');
    localStorage.removeItem('user_info');
    navigate('/login');
  };

  /**
   * Generate chart data for Recovery visualization - with data validation
   */

  /**
   * Generate consistent date range for the last X days
   * This ensures all charts show the same X calendar days, with null values for missing data
   */
  const generateRecoveryChartData = (teamData: Team['recovery_data']) => {
    if (!teamData?.athletes_data || teamData.athletes_data.length === 0) {
      return { labels: [], datasets: [] };
    }

    // Use consistent date range for all charts - last X calendar days ending at today
    const dateLabels = generateDateRange(days);

    // Format labels for display - use simple, clear date format
    // Use noon local time to avoid timezone issues (UTC midnight can show as previous day in western timezones)
    const labels = dateLabels.map(date => {
      const displayDate = new Date(date + 'T12:00:00');
      return displayDate.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric'
      });
    });

    const datasets = teamData.athletes_data
      .filter(athlete => athlete.recovery_data?.some(((data: any) => data && data.recovery_score)))
      .map((athlete) => {
        const borderColor = athleteColorMap.get(athlete.athlete_name) || CHART_COLORS[0];
        const color = { border: borderColor, bg: borderColor + '1A' };
        const isSelected = focusedRecoveryAthlete === athlete.athlete_name;
        const isDimmed = focusedRecoveryAthlete !== null && !isSelected;

        const data = dateLabels.map(date => {
          // Recovery data timestamp is the morning when the score was calculated (wake-up day)
          // No shift needed - the date already represents the correct day
          const dataPoint = athlete.recovery_data?.find(point =>
            point.date && point.date === date && point.recovery_score
          );
          return dataPoint ? dataPoint.recovery_score : null;
        });

        return {
          label: athlete.athlete_name,
          data: data,
          borderColor: isDimmed ? '#9CA3AF' : color.border,
          backgroundColor: isDimmed ? 'rgba(156, 163, 175, 0.05)' : color.bg,
          borderWidth: isSelected ? 4 : isDimmed ? 2 : 3,
          pointBackgroundColor: isDimmed ? '#9CA3AF' : color.border,
          pointBorderColor: '#ffffff',
          pointBorderWidth: 2,
          pointRadius: isSelected ? 5 : isDimmed ? 3 : 4,
          pointHoverRadius: isSelected ? 7 : isDimmed ? 0 : 6,
          pointHitRadius: isDimmed ? 0 : 10,
          fill: false,
          tension: 0.4,
          spanGaps: true,
          borderDash: isDimmed ? [5, 5] : [],
          order: isSelected ? 1 : isDimmed ? 3 : 2, // Selected on top, dimmed behind
        };
      });

    return { labels, datasets };
  };

  /**
   * Generate chart data for Sleep visualization (Quality + Duration) - with data validation
   */
  const generateSleepChartData = (teamData: Team['sleep_data']) => {
    if (!teamData?.athletes_data || teamData.athletes_data.length === 0) {
      return { labels: [], datasets: [] };
    }

    // Use consistent date range for all charts - last X calendar days ending at today
    const dateLabels = generateDateRange(days);

    // Format labels for display - use simple, clear date format
    // Use noon local time to avoid timezone issues (UTC midnight can show as previous day in western timezones)
    const labels = dateLabels.map(date => {
      const displayDate = new Date(date + 'T12:00:00');
      return displayDate.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric'
      });
    });

    const datasets: any[] = [];

    // Quality datasets (solid lines)
    teamData.athletes_data
      .filter(athlete => athlete.sleep_data?.some(((data: any) => data && data.total_duration_hours)))
      .forEach((athlete) => {
        const color = athleteColorMap.get(athlete.athlete_name) || CHART_COLORS[0];
        const isSelected = focusedSleepAthlete === athlete.athlete_name;
        const isDimmed = focusedSleepAthlete !== null && !isSelected;

        const qualityData = dateLabels.map(date => {
          // Sleep timestamp is the wake-up time — date already represents the correct day
          const dataPoint = athlete.sleep_data?.find(point =>
            point.date && point.date === date && point.total_duration_hours
          );
          return dataPoint ? dataPoint.sleep_quality_score : null;
        });

        datasets.push({
          label: athlete.athlete_name,
          data: qualityData,
          borderColor: isDimmed ? '#9CA3AF' : color,
          backgroundColor: isDimmed ? '#9CA3AF' : color,
          borderWidth: isSelected ? 4 : isDimmed ? 2 : 3,
          pointBackgroundColor: isDimmed ? '#9CA3AF' : color,
          pointBorderColor: '#ffffff',
          pointBorderWidth: 2,
          pointRadius: isSelected ? 5 : isDimmed ? 3 : 4,
          pointHoverRadius: isSelected ? 7 : isDimmed ? 0 : 6,
          pointHitRadius: isDimmed ? 0 : 10,
          fill: false,
          tension: 0.4,
          spanGaps: true,
          yAxisID: 'y',
          borderDash: isDimmed ? [5, 5] : [],
          order: isSelected ? 1 : isDimmed ? 3 : 2, // Selected on top, dimmed behind
        });
      });

    // Duration datasets (dashed lines)
    teamData.athletes_data
      .filter(athlete => athlete.sleep_data?.some(((data: any) => data && data.total_duration_hours)))
      .forEach((athlete) => {
        const color = athleteColorMap.get(athlete.athlete_name) || CHART_COLORS[0];
        const isSelected = focusedSleepAthlete === athlete.athlete_name;
        const isDimmed = focusedSleepAthlete !== null && !isSelected;

        const durationData = dateLabels.map(date => {
          // Sleep timestamp is the wake-up time — date already represents the correct day
          const dataPoint = athlete.sleep_data?.find(point =>
            point.date && point.date === date && point.total_duration_hours
          );
          return dataPoint ? dataPoint.total_duration_hours : null;
        });

        datasets.push({
          label: `${athlete.athlete_name} Duration (hidden)`,
          data: durationData,
          borderColor: isDimmed ? '#9CA3AF' : color,
          backgroundColor: isDimmed ? '#9CA3AF' : color,
          borderWidth: isDimmed ? 1.5 : 2,
          borderDash: [5, 5],
          pointBackgroundColor: isDimmed ? '#9CA3AF' : color,
          pointBorderColor: '#ffffff',
          pointBorderWidth: 2,
          pointRadius: isDimmed ? 2 : 3,
          pointHoverRadius: isDimmed ? 0 : 5,
          pointHitRadius: isDimmed ? 0 : 10,
          fill: false,
          tension: 0.4,
          spanGaps: true,
          yAxisID: 'y1',
          hidden: false,
          order: isSelected ? 1 : isDimmed ? 3 : 2, // Selected on top, dimmed behind
        });
      });

    return { labels, datasets };
  };

  /**
   * Get chart options for sleep chart with dual y-axes and filtered legend
   */
  const getSleepChartOptions = () => {
    return {
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        mode: 'index' as const,
        intersect: false,
      },
      onHover: (event: any, elements: any[], chart: any) => {
        const canvas = chart.canvas;
        if (elements.length > 0) {
          // Find the closest element to the mouse position
          const mouseY = event.native?.offsetY ?? event.y;
          let closestElement = elements[0];
          let minDistance = Infinity;

          elements.forEach((el: any) => {
            const meta = chart.getDatasetMeta(el.datasetIndex);
            const point = meta.data[el.index];
            if (point) {
              const distance = Math.abs(point.y - mouseY);
              if (distance < minDistance) {
                minDistance = distance;
                closestElement = el;
              }
            }
          });

          const dataset = chart.data.datasets[closestElement.datasetIndex];
          let athleteName = dataset.label;
          athleteName = athleteName.replace(' Duration (hidden)', '');
          const isDimmed = focusedSleepAthlete !== null && focusedSleepAthlete !== athleteName;
          canvas.style.cursor = isDimmed ? 'not-allowed' : 'pointer';
        } else {
          canvas.style.cursor = 'default';
        }
      },
      onClick: (event: any, elements: any[], chart: any) => {
        if (elements.length > 0) {
          // Find the closest element to the click position
          const mouseY = event.native?.offsetY ?? event.y;
          let closestElement = elements[0];
          let minDistance = Infinity;

          elements.forEach((el: any) => {
            const meta = chart.getDatasetMeta(el.datasetIndex);
            const point = meta.data[el.index];
            if (point) {
              const distance = Math.abs(point.y - mouseY);
              if (distance < minDistance) {
                minDistance = distance;
                closestElement = el;
              }
            }
          });

          const dataset = chart.data.datasets[closestElement.datasetIndex];
          let clickedAthleteName = dataset.label;
          clickedAthleteName = clickedAthleteName.replace(' Duration (hidden)', '');

          // Check if clicked athlete is dimmed - if so, ignore completely
          const isDimmed = focusedSleepAthlete !== null && focusedSleepAthlete !== clickedAthleteName;
          if (isDimmed) {
            return; // Ignore clicks on dimmed athletes
          }

          // Toggle: click on active athlete to deselect, click when nothing selected to select
          setFocusedSleepAthlete(focusedSleepAthlete === clickedAthleteName ? null : clickedAthleteName);
        }
      },
      plugins: {
        legend: {
          display: true,
          position: 'top' as const,
          maxWidth: undefined,
          maxHeight: 150, // Limit legend height for scrolling
          labels: {
            color: isDark ? '#9CA3AF' : '#6B7280',
            font: (context: any) => {
              const dataset = context.chart.data.datasets[context.datasetIndex];
              let athleteName = dataset?.label || '';
              athleteName = athleteName.replace(' Duration (hidden)', '');
              const shouldDim = focusedSleepAthlete !== null && focusedSleepAthlete !== athleteName;

              return {
                size: 11, // Slightly smaller for compact view
                weight: shouldDim ? 400 as const : 500 as const,
                lineThrough: shouldDim, // Add strikethrough for dimmed athletes
              };
            },
            usePointStyle: true,
            pointStyle: 'circle' as const,
            padding: 10, // Reduced padding for compact view
            boxWidth: 10, // Smaller box for compact view
            boxHeight: 10,
            filter: (legendItem: any) => {
              // Hide duration datasets from legend (they contain "Duration (hidden)")
              return !legendItem.text.includes('Duration (hidden)');
            },
          },
          onClick: (event: any, legendItem: any, legend: any) => {
            const clickedAthleteName = legendItem.text;
            const chart = legend.chart;

            // Shift+Click or Double-click: toggle focus
            if (event.shiftKey || event.detail === 2) {
              // Toggle focus
              setFocusedSleepAthlete(focusedSleepAthlete === clickedAthleteName ? null : clickedAthleteName);
            } else {
              // Single click: toggle visibility (Chart.js default behavior)
              const index = legendItem.datasetIndex;
              const meta = chart.getDatasetMeta(index);
              // Toggle both quality and duration datasets for this athlete
              const athleteName = clickedAthleteName;
              chart.data.datasets.forEach((ds: any, idx: number) => {
                const dsName = ds.label.replace(' Duration (hidden)', '');
                if (dsName === athleteName) {
                  const dsMeta = chart.getDatasetMeta(idx);
                  dsMeta.hidden = !dsMeta.hidden;
                }
              });
              chart.update();
            }
          },
        },
        tooltip: {
          mode: 'index' as const,
          intersect: false,
          filter: (tooltipItem: any) => {
            // Only show focused athlete in tooltip, or all if none focused
            let athleteName = tooltipItem.dataset.label;
            athleteName = athleteName.replace(' Duration (hidden)', '');
            return focusedSleepAthlete === null || focusedSleepAthlete === athleteName;
          },
          backgroundColor: isDark ? '#0a0a0a' : '#FFFFFF',
          titleColor: isDark ? '#F3F4F6' : '#1F2937',
          bodyColor: isDark ? '#F3F4F6' : '#1F2937',
          borderColor: isDark ? '#374151' : '#E5E7EB',
          borderWidth: 1,
          cornerRadius: 12,
          titleFont: { weight: 'bold' as const, size: 14 },
          bodyFont: { size: 13 },
          padding: 12,
          callbacks: {
            title: (context: any) => {
              return `${context[0].label}`;
            },
            label: (context: any) => {
              if (context.dataset.label.includes('Duration (hidden)')) {
                const athleteName = context.dataset.label.replace(' Duration (hidden)', '');
                const value = context.parsed.y ? context.parsed.y.toFixed(1) : 'N/A';
                return `${athleteName} Duration: ${value}h`;
              } else {
                const athleteName = context.dataset.label;
                const value = context.parsed.y ? context.parsed.y.toFixed(1) : 'N/A';
                return `${athleteName} Quality: ${value}/100`;
              }
            },
            labelTextColor: (context: any) => {
              // Use the dataset's border color for the label text
              return context.dataset.borderColor || (isDark ? '#F3F4F6' : '#1F2937');
            },
            footer: (context: any) => {
              if (context.length === 0) return '';
              let athleteName = context[0].dataset.label;
              athleteName = athleteName.replace(' Duration (hidden)', '');

              if (focusedSleepAthlete === athleteName) {
                return 'Click to show all athletes';
              }
              return focusedSleepAthlete === null ? 'Click to focus on this athlete' : '';
            },
          },
        },
        zoom: {
          zoom: {
            wheel: {
              enabled: true,
            },
            pinch: {
              enabled: true,
            },
            mode: 'x' as const,
          },
          pan: {
            enabled: true,
            mode: 'x' as const,
          },
        },
      },
      scales: {
        x: {
          display: true,
          grid: {
            display: true,
            color: isDark ? '#374151' : '#F3F4F6'
          },
          ticks: {
            color: isDark ? '#9CA3AF' : '#6B7280',
            font: { size: 12 },
            maxRotation: 0,
            minRotation: 0,
          },
          border: {
            display: false,
          },
        },
        y: {
          type: 'linear' as const,
          display: true,
          position: 'left' as const,
          title: {
            display: true,
            text: 'Sleep Quality',
            color: isDark ? '#9CA3AF' : '#6B7280',
            font: { size: 12, weight: 'bold' as const },
          },
          grid: {
            color: isDark ? '#374151' : '#F3F4F6',
            borderDash: [5, 5],
          },
          ticks: {
            color: isDark ? '#9CA3AF' : '#6B7280',
            font: { size: 12 },
            callback: (value: any) => `${value}/100`,
          },
          border: {
            display: false,
          },
          min: 0,
          max: 100,
        },
        y1: {
          type: 'linear' as const,
          display: true,
          position: 'right' as const,
          title: {
            display: true,
            text: 'Duration (hours)',
            color: isDark ? '#9CA3AF' : '#6B7280',
            font: { size: 12, weight: 'bold' as const },
          },
          grid: {
            drawOnChartArea: false,
          },
          ticks: {
            color: isDark ? '#9CA3AF' : '#6B7280',
            font: { size: 12 },
            callback: (value: any) => `${value}h`,
          },
          border: {
            display: false,
          },
          min: 0,
          max: 12,
        },
      },
    };
  };

  /**
   * Generate chart data for Activity/Strain visualization - with data validation
   */
  const generateActivityChartData = (teamData: Team['activity_data']) => {
    if (!teamData?.athletes_data || teamData.athletes_data.length === 0) {
      return { labels: [], datasets: [] };
    }

    // Use consistent date range for all charts - last X calendar days ending at today
    const labels = generateDateRange(days);

    // For strain, use the highest strain score per day (not sum)
    const athleteData: { [key: string]: { [date: string]: number } } = {};

    teamData.athletes_data.forEach(athlete => {
      if (!athleteData[athlete.athlete_name]) {
        athleteData[athlete.athlete_name] = {};
      }

      // Filter out invalid activity data
      athlete.activity_data?.filter(((data: any) => data && data.strain_score)).forEach(activity => {
        // Activity/strain timestamp is when the workout started - no shift needed
        // The date already represents the correct calendar day
        const displayDateStr = activity.date;

        const strainScore = activity.strain_score || 0;

        // Take the maximum strain for the day (daily strain, not sum of workouts)
        if (!athleteData[athlete.athlete_name][displayDateStr] || athleteData[athlete.athlete_name][displayDateStr] < strainScore) {
          athleteData[athlete.athlete_name][displayDateStr] = strainScore;
        }
      });
    });

    const datasets: any[] = Object.keys(athleteData).map((athleteName) => {
      const color = athleteColorMap.get(athleteName) || CHART_COLORS[0];
      const isSelected = focusedStrainAthlete === athleteName;
      const isDimmed = focusedStrainAthlete !== null && !isSelected;

      const data = labels.map((date: string) => (athleteData[athleteName][date] ?? null));

      return {
        label: athleteName,
        data: data,
        borderColor: isDimmed ? '#9CA3AF' : color,
        backgroundColor: isDimmed ? '#9CA3AF' : color,
        borderWidth: isSelected ? 4 : isDimmed ? 2 : 3,
        pointBackgroundColor: isDimmed ? '#9CA3AF' : color,
        pointBorderColor: '#ffffff',
        pointBorderWidth: 2,
        pointRadius: isSelected ? 5 : isDimmed ? 3 : 4,
        pointHoverRadius: isSelected ? 7 : isDimmed ? 0 : 6,
        pointHitRadius: isDimmed ? 0 : 10,
        fill: false,
        tension: 0.4,
        spanGaps: true,
        borderDash: isDimmed ? [5, 5] : [],
        order: isSelected ? 1 : isDimmed ? 3 : 2, // Selected on top, dimmed behind
      };
    });

    return { labels, datasets };
  };

  /**
   * Generate HRV trend chart data - with data validation
   */
  const generateHRVChartData = (teamData: Team['recovery_data']) => {
    if (!teamData?.athletes_data || teamData.athletes_data.length === 0) {
      return { labels: [], datasets: [] };
    }

    // Use consistent date range for all charts - last X calendar days
    const labels = generateDateRange(days);

    const datasets: any[] = teamData.athletes_data
      .filter(athlete => athlete.recovery_data?.some(((data: any) => data && data.hrv_rmssd_milli)))
      .map((athlete, index) => {
        const color = CHART_COLORS[index % CHART_COLORS.length];

        const data = labels.map((date: string) => {
          // HRV comes from recovery data - timestamp is the morning when recovery was calculated
          // No shift needed - the date already represents the correct day
          const dataPoint = athlete.recovery_data?.find(point =>
            point.date === date && point && point.hrv_rmssd_milli
          );
          return dataPoint?.hrv_rmssd_milli || null;
        });

        return {
          label: athlete.athlete_name,
          data: data,
          borderColor: color,
          backgroundColor: color,
          borderWidth: 3,
          pointBackgroundColor: color,
          pointBorderColor: '#ffffff',
          pointBorderWidth: 2,
          pointRadius: 4,
          pointHoverRadius: 6,
          fill: false,
          tension: 0.4,
          spanGaps: true,
        };
      });

    return { labels, datasets };
  };

  /**
   * Generate sleep architecture analysis (stacked bar chart) - with data validation
   */
  const generateSleepArchitectureData = (teamData: Team['sleep_data']) => {
    if (!teamData?.athletes_data || teamData.athletes_data.length === 0) {
      return { labels: [], datasets: [] };
    }

    // Get average sleep composition for each athlete, using only valid sleep data
    const athletesWithValidData = teamData.athletes_data.filter(athlete =>
      athlete.sleep_data?.some(((data: any) => data && data.total_duration_hours))
    );

    const athleteLabels = athletesWithValidData.map(athlete => athlete.athlete_name);

    const deepSleepData = athletesWithValidData.map(athlete => {
      const validSleepData = athlete.sleep_data?.filter(((data: any) => data && data.total_duration_hours)) || [];
      const deepSleepValues = validSleepData.map(s => s.deep_sleep_hours).filter((v): v is number => typeof v === 'number' && v > 0);
      return deepSleepValues.length > 0 ? deepSleepValues.reduce((a, b) => a + b, 0) / deepSleepValues.length : 0;
    });

    const lightSleepData = athletesWithValidData.map(athlete => {
      const validSleepData = athlete.sleep_data?.filter(((data: any) => data && data.total_duration_hours)) || [];
      const lightSleepValues = validSleepData.map(s => s.light_sleep_hours).filter((v): v is number => typeof v === 'number' && v > 0);
      return lightSleepValues.length > 0 ? lightSleepValues.reduce((a, b) => a + b, 0) / lightSleepValues.length : 0;
    });

    const remSleepData = athletesWithValidData.map(athlete => {
      const validSleepData = athlete.sleep_data?.filter(((data: any) => data && data.total_duration_hours)) || [];
      const remSleepValues = validSleepData.map(s => s.rem_sleep_hours).filter((v): v is number => typeof v === 'number' && v > 0);
      return remSleepValues.length > 0 ? remSleepValues.reduce((a, b) => a + b, 0) / remSleepValues.length : 0;
    });

    const awakeData = athletesWithValidData.map(athlete => {
      const validSleepData = athlete.sleep_data?.filter(((data: any) => data && data.total_duration_hours)) || [];
      const awakeValues = validSleepData.map(s => s.awake_duration_hours).filter((v): v is number => typeof v === 'number' && v > 0);
      return awakeValues.length > 0 ? awakeValues.reduce((a, b) => a + b, 0) / awakeValues.length : 0;
    });

    return {
      labels: athleteLabels,
      datasets: [
        {
          label: 'Deep Sleep',
          data: deepSleepData,
          backgroundColor: '#1E40AF',
          borderColor: '#1E40AF',
          borderWidth: 1,
        },
        {
          label: 'REM Sleep',
          data: remSleepData,
          backgroundColor: '#7C3AED',
          borderColor: '#7C3AED',
          borderWidth: 1,
        },
        {
          label: 'Light Sleep',
          data: lightSleepData,
          backgroundColor: '#06B6D4',
          borderColor: '#06B6D4',
          borderWidth: 1,
        },
        {
          label: 'Awake Time',
          data: awakeData,
          backgroundColor: '#EF4444',
          borderColor: '#EF4444',
          borderWidth: 1,
        },
      ],
    };
  };

  /**
   * Generate heart rate zone distribution data - with data validation
   */
  const generateHeartRateZoneData = (teamData: Team['activity_data']) => {
    if (!teamData?.athletes_data || teamData.athletes_data.length === 0) {
      return { labels: [], datasets: [] };
    }

    // Only include athletes with valid activity data
    const athletesWithValidData = teamData.athletes_data.filter(athlete =>
      athlete.activity_data?.some(((data: any) => data && data.strain_score))
    );

    const athleteLabels = athletesWithValidData.map(athlete => athlete.athlete_name);

    const zones = ['zone_1_minutes', 'zone_2_minutes', 'zone_3_minutes', 'zone_4_minutes', 'zone_5_minutes'];
    const zoneLabels = ['Zone 1 (50-60%)', 'Zone 2 (60-70%)', 'Zone 3 (70-80%)', 'Zone 4 (80-90%)', 'Zone 5 (90-100%)'];
    const zoneColors = ['#10B981', '#F59E0B', '#EF4444', '#DC2626', '#7C2D12'];

    const datasets = zones.map((zone, index) => {
      const data = athletesWithValidData.map(athlete => {
        const validActivityData = athlete.activity_data?.filter(((data: any) => data && data.strain_score)) || [];
        const zoneMinutes = validActivityData.reduce((total, activity) => {
          const zoneTime = activity.zone_duration?.[zone as keyof typeof activity.zone_duration] || 0;
          return total + zoneTime;
        }, 0);
        return zoneMinutes;
      });

      return {
        label: zoneLabels[index],
        data: data,
        backgroundColor: zoneColors[index],
        borderColor: zoneColors[index],
        borderWidth: 1,
      };
    });

    return { labels: athleteLabels, datasets };
  };

  /**
   * Get chart options for different metric types
   */
  const getMetricChartOptions = (metricType: 'recovery' | 'sleep' | 'strain') => {
    const baseOptions = {
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        intersect: false,
        mode: 'index' as const,
      },
      onHover: (event: any, elements: any[], chart: any) => {
        const canvas = chart.canvas;
        if (elements.length > 0) {
          // Find the closest element to the mouse position
          const mouseY = event.native?.offsetY ?? event.y;
          let closestElement = elements[0];
          let minDistance = Infinity;

          elements.forEach((el: any) => {
            const meta = chart.getDatasetMeta(el.datasetIndex);
            const point = meta.data[el.index];
            if (point) {
              const distance = Math.abs(point.y - mouseY);
              if (distance < minDistance) {
                minDistance = distance;
                closestElement = el;
              }
            }
          });

          const dataset = chart.data.datasets[closestElement.datasetIndex];
          const athleteName = dataset.label;
          const currentFocused = metricType === 'recovery' ? focusedRecoveryAthlete :
                                metricType === 'sleep' ? focusedSleepAthlete :
                                focusedStrainAthlete;
          const isDimmed = currentFocused !== null && currentFocused !== athleteName;
          canvas.style.cursor = isDimmed ? 'not-allowed' : 'pointer';
        } else {
          canvas.style.cursor = 'default';
        }
      },
      onClick: (event: any, elements: any[], chart: any) => {
        if (elements.length > 0) {
          // Find the closest element to the click position
          const mouseY = event.native?.offsetY ?? event.y;
          let closestElement = elements[0];
          let minDistance = Infinity;

          elements.forEach((el: any) => {
            const meta = chart.getDatasetMeta(el.datasetIndex);
            const point = meta.data[el.index];
            if (point) {
              const distance = Math.abs(point.y - mouseY);
              if (distance < minDistance) {
                minDistance = distance;
                closestElement = el;
              }
            }
          });

          const dataset = chart.data.datasets[closestElement.datasetIndex];
          const clickedAthleteName = dataset.label;

          // Get current focused athlete for this metric
          const currentFocused = metricType === 'recovery' ? focusedRecoveryAthlete :
                                metricType === 'sleep' ? focusedSleepAthlete :
                                focusedStrainAthlete;

          // Check if clicked athlete is dimmed - if so, ignore completely
          const isDimmed = currentFocused !== null && currentFocused !== clickedAthleteName;
          if (isDimmed) {
            return; // Ignore clicks on dimmed athletes
          }

          // Toggle: click on active athlete to deselect, click when nothing selected to select
          if (metricType === 'recovery') {
            setFocusedRecoveryAthlete(currentFocused === clickedAthleteName ? null : clickedAthleteName);
          } else if (metricType === 'sleep') {
            setFocusedSleepAthlete(currentFocused === clickedAthleteName ? null : clickedAthleteName);
          } else if (metricType === 'strain') {
            setFocusedStrainAthlete(currentFocused === clickedAthleteName ? null : clickedAthleteName);
          }
        }
      },
      plugins: {
        legend: {
          display: true,
          position: 'top' as const,
          maxWidth: undefined,
          maxHeight: 150, // Limit legend height for scrolling
          labels: {
            color: isDark ? '#9CA3AF' : '#6B7280',
            font: (context: any) => {
              const athleteName = context.chart.data.datasets[context.datasetIndex]?.label;
              const focusedAthlete = metricType === 'recovery' ? focusedRecoveryAthlete :
                                    metricType === 'sleep' ? focusedSleepAthlete :
                                    focusedStrainAthlete;
              const shouldDim = focusedAthlete !== null && focusedAthlete !== athleteName;

              return {
                size: 11, // Slightly smaller for compact view
                weight: shouldDim ? 400 as const : 500 as const,
                lineThrough: shouldDim, // Add strikethrough for dimmed athletes
              };
            },
            usePointStyle: true,
            pointStyle: 'circle' as const,
            padding: 10, // Reduced padding for compact view
            boxWidth: 10, // Smaller box for compact view
            boxHeight: 10,
          },
          onClick: (event: any, legendItem: any, legend: any) => {
            const clickedAthleteName = legendItem.text;
            const chart = legend.chart;

            // Get current focused athlete for this metric
            const currentFocused = metricType === 'recovery' ? focusedRecoveryAthlete :
                                  metricType === 'sleep' ? focusedSleepAthlete :
                                  focusedStrainAthlete;

            // Shift+Click or Double-click: toggle focus
            if (event.shiftKey || event.detail === 2) {
              // Toggle focus
              if (metricType === 'recovery') {
                setFocusedRecoveryAthlete(currentFocused === clickedAthleteName ? null : clickedAthleteName);
              } else if (metricType === 'sleep') {
                setFocusedSleepAthlete(currentFocused === clickedAthleteName ? null : clickedAthleteName);
              } else if (metricType === 'strain') {
                setFocusedStrainAthlete(currentFocused === clickedAthleteName ? null : clickedAthleteName);
              }
            } else {
              // Single click: toggle visibility (Chart.js default behavior)
              const index = legendItem.datasetIndex;
              const meta = chart.getDatasetMeta(index);
              meta.hidden = !meta.hidden;
              chart.update();
            }
          },
        },
        tooltip: {
          mode: 'index' as const,
          intersect: false,
          filter: (tooltipItem: any) => {
            // Only show focused athlete in tooltip, or all if none focused
            const athleteName = tooltipItem.dataset.label;
            const focusedAthlete = metricType === 'recovery' ? focusedRecoveryAthlete :
                                  metricType === 'sleep' ? focusedSleepAthlete :
                                  focusedStrainAthlete;
            return focusedAthlete === null || focusedAthlete === athleteName;
          },
          backgroundColor: isDark ? '#0a0a0a' : '#FFFFFF',
          titleColor: isDark ? '#F3F4F6' : '#1F2937',
          bodyColor: isDark ? '#F3F4F6' : '#1F2937',
          borderColor: isDark ? '#374151' : '#E5E7EB',
          borderWidth: 1,
          cornerRadius: 12,
          titleFont: { weight: 'bold' as const, size: 14 },
          bodyFont: { size: 13 },
          padding: 12,
          callbacks: {
            title: (context: any) => {
              return `${context[0].label}`;
            },
            label: (context: any) => {
              let suffix = '';
              if (metricType === 'recovery') suffix = '%';
              else if (metricType === 'sleep') suffix = '/100';
              else if (metricType === 'strain') suffix = '';

              // Show athlete name prominently with value
              const athleteName = context.dataset.label || 'Unknown';
              const value = context.parsed.y ? context.parsed.y.toFixed(1) : 'N/A';
              return `${athleteName}: ${value}${suffix}`;
            },
            labelTextColor: (context: any) => {
              // Use the dataset's border color for the label text
              return context.dataset.borderColor || (isDark ? '#F3F4F6' : '#1F2937');
            },
            footer: (context: any) => {
              if (context.length === 0) return '';
              const athleteName = context[0].dataset.label;
              const focusedAthlete = metricType === 'recovery' ? focusedRecoveryAthlete :
                                    metricType === 'sleep' ? focusedSleepAthlete :
                                    focusedStrainAthlete;

              if (focusedAthlete === athleteName) {
                return 'Click to show all athletes';
              }
              return focusedAthlete === null ? 'Click to focus on this athlete' : '';
            },
          },
        },
        zoom: {
          zoom: {
            wheel: {
              enabled: true,
            },
            pinch: {
              enabled: true,
            },
            mode: 'x' as const,
          },
          pan: {
            enabled: true,
            mode: 'x' as const,
          },
        },
      },
      scales: {
        x: {
          display: true,
          grid: {
            display: true,
            color: isDark ? '#374151' : '#F3F4F6'
          },
          ticks: {
            color: isDark ? '#9CA3AF' : '#6B7280',
            font: { size: 12 },
            maxRotation: 0,
            minRotation: 0,
            autoSkip: true,
            maxTicksLimit: 8,  // Limit to ~8 ticks for readability
            padding: 10,  // Add padding to prevent cutoff
          },
          border: {
            display: false,
          },
          // For line charts, offset should be false to span full width
          offset: false,
        },
        y: {
          display: true,
          grid: {
            display: true,
            color: isDark ? '#374151' : '#F3F4F6',
            borderDash: [5, 5],
          },
          ticks: {
            color: isDark ? '#9CA3AF' : '#6B7280',
            font: { size: 12 },
            callback: (value: any) => {
              if (metricType === 'recovery') return `${value}%`;
              else if (metricType === 'sleep') return `${value}/100`;
              else if (metricType === 'strain') return `${value}`;
              return value;
            },
          },
          border: {
            display: false,
          },
        },
      },
    };

    // Customize Y-axis ranges based on metric type
    if (metricType === 'recovery') {
      (baseOptions.scales.y as any).min = 0;
      (baseOptions.scales.y as any).max = 100;
    } else if (metricType === 'sleep') {
      (baseOptions.scales.y as any).min = 0;
      (baseOptions.scales.y as any).max = 100;
    } else if (metricType === 'strain') {
      (baseOptions.scales.y as any).min = 0;
      (baseOptions.scales.y as any).max = 21;
    }

    return baseOptions;
  };

  /**
   * Generate rankings for athlete comparison tables
   */
  const generateRankings = () => {
    if (!selectedTeam) return {
      recovery: [],
      sleep: [],
      strain: [],
      hrv: [],
    };

    // Recovery Rankings - with proper data validation
    const recoveryRankings = selectedTeam.recovery_data?.athletes_data
      .filter(athlete => athlete.recovery_data && athlete.recovery_data.length > 0)
      .map(athlete => {
        // Filter out invalid/non-wearing data points
        const validRecoveryData = athlete.recovery_data?.filter(((data: any) => data && data.recovery_score)) || [];

        const recoveryScores = validRecoveryData.map(r => r.recovery_score);
        const rhrValues = validRecoveryData.map(r => r.resting_heart_rate);

        const averageRecovery = recoveryScores.length > 0 ? recoveryScores.reduce((a, b) => a + b, 0) / recoveryScores.length : 0;
        const latestRecovery = recoveryScores.length > 0 ? recoveryScores[recoveryScores.length - 1] : 0;

        // Calculate Recovery trend: difference between latest and average
        const recoveryTrend = recoveryScores.length > 1 ? latestRecovery - averageRecovery : 0;

        return {
          name: athlete.athlete_name,
          averageRecovery: averageRecovery,
          latestRecovery: latestRecovery,
          recoveryTrend: Math.round(recoveryTrend * 10) / 10,
          averageRHR: rhrValues.length > 0 ? rhrValues.filter((v): v is number => typeof v === 'number').reduce((a, b) => a + b, 0) / rhrValues.filter((v): v is number => typeof v === 'number').length : 0,
          dataPoints: validRecoveryData.length,
        };
      })
      .filter(ranking => ranking.dataPoints > 0) // Only include athletes with valid data
      .sort((a, b) => b.averageRecovery - a.averageRecovery) || [];

    // HRV Rankings - with proper data validation
    const hrvRankings = selectedTeam.recovery_data?.athletes_data
      .filter(athlete => athlete.recovery_data && athlete.recovery_data.length > 0)
      .map(athlete => {
        // Filter out invalid HRV data points
        const validHRVData = athlete.recovery_data?.filter(((data: any) => data && data.hrv_rmssd_milli)) || [];
        const hrvValues = validHRVData.map(r => r.hrv_rmssd_milli);

        const averageHRV = hrvValues.length > 0 ? hrvValues.filter((v): v is number => typeof v === 'number').reduce((a, b) => a + b, 0) / hrvValues.filter((v): v is number => typeof v === 'number').length : 0;
        const safeHRVValues = hrvValues.filter((v): v is number => typeof v === 'number');
        const latestHRV = safeHRVValues.length > 0 ? safeHRVValues[safeHRVValues.length - 1] : 0;

        // Calculate HRV trend: difference between latest and average
        const hrvTrend = hrvValues.length > 1 ? latestHRV - averageHRV : 0;

        return {
          name: athlete.athlete_name,
          averageHRV: Math.round(averageHRV),
          latestHRV: Math.round(latestHRV),
          hrvTrend: Math.round(hrvTrend * 10) / 10,
          consistency: safeHRVValues.length > 1 && averageHRV > 0 ? Math.max(0, Math.round((1 - ((safeHRVValues.length > 0 ? Math.max(...safeHRVValues) : 0) - (safeHRVValues.length > 0 ? Math.min(...safeHRVValues) : 0)) / averageHRV) * 100)) : 0,
          dataPoints: validHRVData.length,
        };
      })
      .filter(ranking => ranking.dataPoints > 0) // Only include athletes with valid HRV data
      .sort((a, b) => b.averageHRV - a.averageHRV) || [];

    // Enhanced Sleep Rankings - with proper data validation
    const sleepRankings = selectedTeam.sleep_data?.athletes_data
      .filter(athlete => athlete.sleep_data && athlete.sleep_data.length > 0)
      .map(athlete => {
        // Filter out invalid sleep data (including naps and unrealistic values)
        const validSleepData = athlete.sleep_data?.filter(((data: any) => data && data.total_duration_hours)) || [];

        const qualityScores = validSleepData.map(s => s.sleep_quality_score).filter(v => v != null && v > 0);
        const durations = validSleepData.map(s => s.total_duration_hours);
        const efficiencyScores = validSleepData.map(s => s.sleep_efficiency).filter(v => v != null && v > 0);

        return {
          name: athlete.athlete_name,
          averageQuality: qualityScores.length > 0 ? Math.round(qualityScores.filter((v): v is number => typeof v === 'number').reduce((a, b) => a + b, 0) / qualityScores.filter((v): v is number => typeof v === 'number').length) : 0,
          averageDuration: durations.length > 0 ? Math.round((durations.reduce((a, b) => a + b, 0) / durations.length) * 10) / 10 : 0,
          averageEfficiency: efficiencyScores.length > 0 ? Math.round(efficiencyScores.filter((v): v is number => typeof v === 'number').reduce((a, b) => a + b, 0) / efficiencyScores.filter((v): v is number => typeof v === 'number').length) : 0,
          consistency: validSleepData.length > 1 ? Math.round(validSleepData.reduce((sum, s) => sum + (s.sleep_consistency_percentage || 0), 0) / validSleepData.length) : 0,
          dataPoints: validSleepData.length,
        };
      })
      .filter(ranking => ranking.dataPoints > 0) // Only include athletes with valid sleep data
      .sort((a, b) => b.averageQuality - a.averageQuality) || [];

    // Enhanced Strain Rankings - with proper data validation
    const strainRankings = selectedTeam.activity_data?.athletes_data
      .filter(athlete => athlete.activity_data && athlete.activity_data.length > 0)
      .map(athlete => {
        // Filter out invalid activity data
        const validActivityData = athlete.activity_data?.filter(((data: any) => data && data.strain_score)) || [];

        const strainScores = validActivityData.map(a => a.strain_score);
        const heartRates = validActivityData.map(a => a.average_heart_rate).filter(v => v != null && v > 0);
        const maxHeartRates = validActivityData.map(a => a.max_heart_rate).filter(v => v != null && v > 0);

        return {
          name: athlete.athlete_name,
          averageStrain: strainScores.length > 0 ? Math.round((strainScores.filter((v): v is number => typeof v === 'number').reduce((a, b) => a + b, 0) / strainScores.filter((v): v is number => typeof v === 'number').length) * 10) / 10 : 0,
          totalStrain: Math.round((strainScores.filter((v): v is number => typeof v === 'number').reduce((a, b) => a + b, 0)) * 10) / 10,
          latestStrain: strainScores.length > 0 ? strainScores[strainScores.length - 1] : 0,
          averageHR: heartRates.length > 0 ? Math.round(heartRates.filter((v): v is number => typeof v === 'number').reduce((a, b) => a + b, 0) / heartRates.filter((v): v is number => typeof v === 'number').length) : 0,
          maxHRPeak: maxHeartRates.length > 0 ? (maxHeartRates.filter((v): v is number => typeof v === 'number').length > 0 ? Math.max(...maxHeartRates.filter((v): v is number => typeof v === 'number')) : 0) : 0,
          workoutsCount: validActivityData.length,
          dataPoints: validActivityData.length,
        };
      })
      .filter(ranking => ranking.dataPoints > 0) // Only include athletes with valid activity data
      .sort((a, b) => b.totalStrain - a.totalStrain) || [];

    return {
      recovery: recoveryRankings,
      sleep: sleepRankings,
      strain: strainRankings,
      hrv: hrvRankings,
    };
  };

  /**
   * Comprehensive team analysis for insights and alerts
   */
  const generateTeamInsights = () => {
    if (!selectedTeam) return {
      athletesInTheRed: [],
      topPerformers: [],
      teamHealthScore: 0,
      keyInsights: [],
      actionItems: [],
      dataQualityIssues: [],
      recentAlerts: []
    };

    const rankings = generateRankings();
    const athletes = selectedTeam.recovery_data?.athletes_data || [];

    // Athletes "In the Red" - concerning metrics
    const athletesInTheRed = athletes.filter(athlete => {
      const recoveryRanking = rankings.recovery.find(r => r.name === athlete.athlete_name);
      const sleepRanking = rankings.sleep.find(s => s.name === athlete.athlete_name);
      const strainRanking = rankings.strain.find(s => s.name === athlete.athlete_name);
      const hrvRanking = rankings.hrv.find(h => h.name === athlete.athlete_name);

      // Define "red" criteria
      const lowRecovery = recoveryRanking && recoveryRanking.latestRecovery < 33;
      const poorSleep = sleepRanking && (sleepRanking.averageQuality < 60 || sleepRanking.averageDuration < 6.5);
      const highStrain = strainRanking && strainRanking.averageStrain > 15;
      const negativeRecoveryTrend = recoveryRanking && recoveryRanking.recoveryTrend < -10;
      const negativeHRVTrend = hrvRanking && hrvRanking.hrvTrend < -5;
      const lowDataPoints = recoveryRanking && recoveryRanking.dataPoints < 3;

      return lowRecovery || poorSleep || highStrain || negativeRecoveryTrend || negativeHRVTrend || (isAnyWearableConnected(athlete) && lowDataPoints);
    }).map(athlete => {
      const recoveryRanking = rankings.recovery.find(r => r.name === athlete.athlete_name);
      const sleepRanking = rankings.sleep.find(s => s.name === athlete.athlete_name);
      const strainRanking = rankings.strain.find(s => s.name === athlete.athlete_name);
      const hrvRanking = rankings.hrv.find(h => h.name === athlete.athlete_name);

      const issues = [];
      if (recoveryRanking && recoveryRanking.latestRecovery < 33) issues.push('Low Recovery');
      if (sleepRanking && sleepRanking.averageQuality < 60) issues.push('Poor Sleep Quality');
      if (sleepRanking && sleepRanking.averageDuration < 6.5) issues.push('Insufficient Sleep');
      if (strainRanking && strainRanking.averageStrain > 15) issues.push('High Strain');
      if (recoveryRanking && recoveryRanking.recoveryTrend < -10) issues.push('Declining Recovery');
      if (hrvRanking && hrvRanking.hrvTrend < -5) issues.push('Declining HRV');
      if (recoveryRanking && recoveryRanking.dataPoints < 3) issues.push('Limited Data');
      if (!isAnyWearableConnected(athlete)) issues.push('Not Connected');

      // BUGFIX: Ensure we're using recovery score, not HRV value
      // Recovery scores are 0-100%, HRV is typically 20-100ms
      // If we accidentally get HRV value here, it would show as low percentage
      const safeLatestRecovery = recoveryRanking?.latestRecovery || 0;

      // Defensive check: Recovery should be 0-100, HRV is typically < 200
      // If value looks like HRV (< 200 ms), don't display it as recovery
      const displayRecovery = safeLatestRecovery > 100 ? 0 : safeLatestRecovery;

      return {
        name: athlete.athlete_name,
        issues: issues,
        severity: issues.length >= 3 ? 'high' : issues.length >= 2 ? 'medium' : 'low',
        latestRecovery: displayRecovery,
        averageSleep: sleepRanking?.averageQuality || 0,
        averageStrain: strainRanking?.averageStrain || 0,
        // Add HRV for debugging
        latestHRV: hrvRanking?.latestHRV || 0,
      };
    });

    // Top Performers - athletes doing well
    const topPerformers = athletes.filter(athlete => {
      const recoveryRanking = rankings.recovery.find(r => r.name === athlete.athlete_name);
      const sleepRanking = rankings.sleep.find(s => s.name === athlete.athlete_name);
      const hrvRanking = rankings.hrv.find(h => h.name === athlete.athlete_name);

      const goodRecovery = recoveryRanking && recoveryRanking.latestRecovery >= 67;
      const goodSleep = sleepRanking && sleepRanking.averageQuality >= 75 && sleepRanking.averageDuration >= 7.5;
      const positiveRecoveryTrend = recoveryRanking && recoveryRanking.recoveryTrend > 5;
      const positiveHRVTrend = hrvRanking && hrvRanking.hrvTrend > 3;
      const consistentData = recoveryRanking && recoveryRanking.dataPoints >= days * 0.7;

      return (goodRecovery || goodSleep || positiveRecoveryTrend || positiveHRVTrend) && consistentData;
    }).map(athlete => {
      const recoveryRanking = rankings.recovery.find(r => r.name === athlete.athlete_name);
      const sleepRanking = rankings.sleep.find(s => s.name === athlete.athlete_name);

      return {
        name: athlete.athlete_name,
        latestRecovery: recoveryRanking?.latestRecovery || 0,
        averageSleep: sleepRanking?.averageQuality || 0,
        recoveryTrend: recoveryRanking?.recoveryTrend || 0
      };
    }).sort((a, b) => b.latestRecovery - a.latestRecovery).slice(0, 5);

    // Team Health Score (0-100)
    const connectedAthletes = athletes.filter(a => isAnyWearableConnected(a));
    const avgRecovery = rankings.recovery.length > 0 ?
      rankings.recovery.reduce((sum, r) => sum + r.latestRecovery, 0) / rankings.recovery.length : 0;
    const avgSleepQuality = rankings.sleep.length > 0 ?
      rankings.sleep.reduce((sum, s) => sum + s.averageQuality, 0) / rankings.sleep.length : 0;
    const dataCompletenessScore = connectedAthletes.length > 0 ?
      (connectedAthletes.filter(a => (rankings.recovery.find(r => r.name === a.athlete_name)?.dataPoints || 0) >= Math.min(days * 0.3, 7)).length / connectedAthletes.length) * 100 : 0;

    const teamHealthScore = Math.round((avgRecovery + avgSleepQuality + dataCompletenessScore) / 3);

    // Calculate Team Trends
    const calculateTeamTrends = () => {
      // Recovery Trend - compare first half vs second half of period
      const recoveryTrend = (() => {
        if (rankings.recovery.length === 0) return 0;
        const avgTrend = rankings.recovery.reduce((sum, r) => sum + (r.recoveryTrend || 0), 0) / rankings.recovery.length;
        return avgTrend;
      })();

      // Sleep Quality Trend - analyze if team sleep is improving
      const sleepTrend = (() => {
        if (!selectedTeam?.sleep_data?.athletes_data) return 0;

        let totalTrend = 0;
        let athleteCount = 0;

        selectedTeam.sleep_data.athletes_data.forEach(athlete => {
          const validSleep = athlete.sleep_data?.filter(((data: any) => data && data.total_duration_hours)) || [];
          if (validSleep.length >= 4) {
            const mid = Math.floor(validSleep.length / 2);
            const firstHalf = validSleep.slice(0, mid);
            const secondHalf = validSleep.slice(mid);

            const firstAvg = firstHalf.reduce((sum, s) => sum + (s.sleep_quality_score || 0), 0) / firstHalf.length;
            const secondAvg = secondHalf.reduce((sum, s) => sum + (s.sleep_quality_score || 0), 0) / secondHalf.length;

            totalTrend += (secondAvg - firstAvg);
            athleteCount++;
          }
        });

        return athleteCount > 0 ? totalTrend / athleteCount : 0;
      })();

      // Strain Trend - analyze if team strain is stable, increasing, or decreasing
      const strainTrend = (() => {
        if (!selectedTeam?.activity_data?.athletes_data) return 0;

        let totalTrend = 0;
        let athleteCount = 0;

        selectedTeam.activity_data.athletes_data.forEach(athlete => {
          const validActivity = athlete.activity_data?.filter(((data: any) => data && data.strain_score)) || [];
          if (validActivity.length >= 4) {
            const mid = Math.floor(validActivity.length / 2);
            const firstHalf = validActivity.slice(0, mid);
            const secondHalf = validActivity.slice(mid);

            const firstAvg = firstHalf.reduce((sum, a) => sum + (a.strain_score || 0), 0) / firstHalf.length;
            const secondAvg = secondHalf.reduce((sum, a) => sum + (a.strain_score || 0), 0) / secondHalf.length;

            totalTrend += (secondAvg - firstAvg);
            athleteCount++;
          }
        });

        return athleteCount > 0 ? totalTrend / athleteCount : 0;
      })();

      return {
        recoveryTrend: Math.round(recoveryTrend * 10) / 10,
        sleepTrend: Math.round(sleepTrend * 10) / 10,
        strainTrend: Math.round(strainTrend * 10) / 10
      };
    };

    const teamTrends = calculateTeamTrends();

    // Key Insights
    const keyInsights = [];
    if (athletesInTheRed.length > 0) {
      keyInsights.push(`${athletesInTheRed.length} athlete${athletesInTheRed.length > 1 ? 's' : ''} need immediate attention`);
    }
    if (topPerformers.length > 0) {
      keyInsights.push(`${topPerformers.length} athlete${topPerformers.length > 1 ? 's are' : ' is'} performing exceptionally well`);
    }
    if (avgRecovery < 50) {
      keyInsights.push('Team recovery is below optimal levels');
    } else if (avgRecovery > 70) {
      keyInsights.push('Team recovery is excellent');
    }
    if (avgSleepQuality < 65) {
      keyInsights.push('Team sleep quality needs improvement');
    }

    // Add trend-based insights
    if (teamTrends.recoveryTrend > 5) {
      keyInsights.push('Team recovery is trending upward - excellent progress!');
    } else if (teamTrends.recoveryTrend < -5) {
      keyInsights.push('Team recovery is declining - consider reducing training load');
    }

    if (teamTrends.sleepTrend > 3) {
      keyInsights.push('Team sleep quality is improving over time');
    } else if (teamTrends.sleepTrend < -3) {
      keyInsights.push('Team sleep quality is declining - implement sleep hygiene protocols');
    }

    if (teamTrends.strainTrend > 2) {
      keyInsights.push('Training intensity is increasing - monitor for overtraining');
    } else if (teamTrends.strainTrend < -2) {
      keyInsights.push('Training strain is decreasing - good for recovery phase');
    }

    // Action Items
    const actionItems = [];
    if (athletesInTheRed.length > 0) {
      actionItems.push(`Review training load for ${athletesInTheRed.length} athlete${athletesInTheRed.length > 1 ? 's' : ''} showing concerning metrics`);
    }
    const disconnectedCount = athletes.filter(a => !isAnyWearableConnected(a)).length;
    if (disconnectedCount > 0) {
      actionItems.push(`Connect ${disconnectedCount} athlete${disconnectedCount > 1 ? 's' : ''} to wearable devices`);
    }
    if (avgSleepQuality < 65) {
      actionItems.push('Implement sleep hygiene education program');
    }

    // Data Quality Issues
    const dataQualityIssues = athletes.filter(athlete => {
      if (!isAnyWearableConnected(athlete)) return true;
      const recoveryRanking = rankings.recovery.find(r => r.name === athlete.athlete_name);
      return recoveryRanking && recoveryRanking.dataPoints < Math.min(days * 0.3, 7);
    }).map(athlete => ({
      name: athlete.athlete_name,
      issue: !isAnyWearableConnected(athlete) ? 'Not connected to wearable device' : 'Insufficient recent data',
      connected: isAnyWearableConnected(athlete)
    }));

    return {
      athletesInTheRed,
      topPerformers,
      teamHealthScore,
      keyInsights,
      actionItems,
      dataQualityIssues,
      recentAlerts: athletesInTheRed.filter(a => a.severity === 'high').slice(0, 3),
      teamTrends
    };
  };

  const rankings = generateRankings();
  const teamInsights = generateTeamInsights();

  /**
   * Render ranking table component
   */
  const RankingTable: React.FC<{
    title: string;
    data: any[];
    columns: { key: string; label: string; format?: (value: any) => string; tooltip?: string }[];
    icon: string;
    color: string;
  }> = ({ title, data, columns, icon, color }) => (
    <div className={`cgm-card rounded-xl border ${isDark ? 'border-gray-700/50' : 'border-gray-200/50'} shadow-xl overflow-hidden`}>
      <div className="flex items-start sm:items-center justify-between px-4 pt-4 pb-3 gap-3">
        <div className="flex items-center space-x-2 min-w-0 flex-1">
          <div className={`w-8 h-8 ${color} rounded-lg flex items-center justify-center shrink-0`}>
            {renderIcon(icon, 'w-5 h-5 text-white')}
          </div>
          <h3 className="text-base sm:text-lg font-bold cgm-text-primary truncate">{title}</h3>
        </div>
        <div className={`cgm-list-item px-2 py-1 rounded-md shrink-0`}>
          <span className="text-xs font-medium cgm-text-secondary whitespace-nowrap">
            {data.length} {data.length === 1 ? 'athlete' : 'athletes'}
          </span>
        </div>
      </div>

      {data.length > 0 ? (
        <div className="cgm-table-wrap">
          <table className="cgm-table">
            <thead>
              <tr>
                <th className="w-12" title="Athlete's position in rankings">Rank</th>
                <th className="min-w-[100px]" title="Athlete name">Athlete</th>
                {columns.map(column => (
                  <th key={column.key} className="text-right whitespace-nowrap" title={column.tooltip}>
                    {column.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.map((item, index) => (
                <tr key={index}>
                  <td>
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${index === 0 ? 'bg-yellow-500 text-black' :
                      index === 1 ? 'bg-gray-400 text-gray-900' :
                        index === 2 ? 'bg-orange-500 text-white' :
                          'cgm-bg-secondary cgm-text-primary'
                      }`}>
                      {index + 1}
                    </div>
                  </td>
                  <td className="font-medium">
                    <div className="truncate max-w-[100px]" title={item.name}>
                      {item.name}
                    </div>
                  </td>
                  {columns.map(column => (
                    <td key={column.key} className="text-right cgm-text-secondary font-medium whitespace-nowrap">
                      {column.format ? column.format(item[column.key]) : (item[column.key] || 'N/A')}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-center py-8 px-4">
          <div className={`w-12 h-12 ${isDark ? 'bg-gray-700' : 'bg-gray-100'} rounded-full flex items-center justify-center mx-auto mb-3`}>
            <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <h4 className="text-sm font-medium cgm-text-primary mb-1">No Data Available</h4>
          <p className="text-xs cgm-text-secondary max-w-sm mx-auto">
            No athletes have data for this metric in the selected time period.
          </p>
        </div>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen cgm-bg-primary">
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center space-y-8">
            {/* Pulsing Wave Loading Dots */}
            <div className="relative flex items-center justify-center">
              <div className="flex space-x-2">
                <div className={`w-3 h-3 ${isDark ? 'bg-white' : 'bg-black'} rounded-full loading-dot-wave`}
                  style={{ animationDelay: '0ms' }}></div>
                <div className={`w-3 h-3 ${isDark ? 'bg-white' : 'bg-black'} rounded-full loading-dot-wave`}
                  style={{ animationDelay: '200ms' }}></div>
                <div className={`w-3 h-3 ${isDark ? 'bg-white' : 'bg-black'} rounded-full loading-dot-wave`}
                  style={{ animationDelay: '400ms' }}></div>
              </div>
            </div>

            {/* Loading text */}
            <div className="space-y-3">
              <h3 className="text-3xl font-bold cgm-text-primary">Loading Team Dashboard</h3>
              <p className="text-lg cgm-text-secondary">Gathering team analytics...</p>
            </div>
          </div>
        </div>

        {/* Wave Animation Styles */}
        <style>{`
          .loading-dot-wave {
            animation: wave-pulse 1.4s ease-in-out infinite;
          }

          @keyframes wave-pulse {
            0%, 60%, 100% {
              transform: scale(1) translateY(0);
              opacity: 0.7;
            }
            30% {
              transform: scale(1.3) translateY(-8px);
              opacity: 1;
            }
          }
        `}</style>
      </div>
    );
  }

  // Enhanced error state
  if (error) {
    return (
      <div className="min-h-screen cgm-bg-primary flex items-center justify-center p-6">
        <div className="max-w-md w-full">
          <div className={`cgm-card rounded-2xl p-8 text-center`}>
            <div className="flex items-center justify-center mb-4">
              <svg className={`w-12 h-12 cgm-icon-warning`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h2 className={`text-2xl font-bold ${isDark ? 'text-red-400' : 'text-red-600'} mb-4`}>Something went wrong</h2>
            <p className="cgm-text-secondary mb-6 leading-relaxed">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="cgm-button px-6 py-3 rounded-xl font-medium transition-all duration-200 transform hover:scale-105"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Enhanced no teams state
  if (teamsData.length === 0) {
    return (
      <div className="min-h-screen cgm-bg-primary">
        <Header
          userInfo={userInfo}
          isDark={isDark}
          commands={commands}
        />

        <DashboardLayout>
          <div className="text-center">
            <div className={`w-24 h-24 ${isDark ? 'bg-gray-800' : 'bg-gray-100'} rounded-full flex items-center justify-center mx-auto mb-6`}>
              <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <h2 className="text-3xl font-bold cgm-text-primary mb-4">No Teams Found</h2>
            <p className="text-lg cgm-text-secondary mb-8 max-w-2xl mx-auto leading-relaxed">
              You haven't been assigned to any teams as a coach yet. Contact your administrator to get started with team management.
            </p>
            <Link
              to="/dashboard"
              className={`inline-flex items-center space-x-2 px-8 py-4 rounded-xl font-semibold transition-all duration-200 transform hover:scale-105 ${isDark ? 'bg-white text-black hover:bg-gray-100' : 'bg-gray-900 text-white hover:bg-gray-800'}`}
            >
              <LayoutDashboard className="w-5 h-5" />
              <span>Go to Personal Dashboard</span>
            </Link>
          </div>
        </DashboardLayout>
      </div>
    );
  }

  /**
   * Handle double-click on athlete to navigate to athlete detail page
   */
  const handleAthleteDoubleClick = (athleteId: number) => {
    if (selectedTeam) {
      navigate(`/athlete/${selectedTeam.id}/${athleteId}`);
    }
  };

  // Filtered chart data generators
  const generateFilteredRecoveryChartData = (teamData: Team['recovery_data']) => {
    if (!teamData?.athletes_data || teamData.athletes_data.length === 0) {
      return { labels: [], datasets: [] };
    }
    const filtered = {
      ...teamData,
      athletes_data: teamData.athletes_data.filter(a => selectedRecoveryAthletes.includes(a.athlete_name)),
    };
    return generateRecoveryChartData(filtered);
  };
  const generateFilteredSleepChartData = (teamData: Team['sleep_data']) => {
    if (!teamData?.athletes_data || teamData.athletes_data.length === 0) {
      return { labels: [], datasets: [] };
    }
    const filtered = {
      ...teamData,
      athletes_data: teamData.athletes_data.filter(a => selectedSleepAthletes.includes(a.athlete_name)),
    };
    return generateSleepChartData(filtered);
  };
  const generateFilteredActivityChartData = (teamData: Team['activity_data']) => {
    if (!teamData?.athletes_data || teamData.athletes_data.length === 0) {
      return { labels: [], datasets: [] };
    }
    const filtered = {
      ...teamData,
      athletes_data: teamData.athletes_data.filter(a => selectedStrainAthletes.includes(a.athlete_name)),
    };
    return generateActivityChartData(filtered);
  };

  // For dropdowns, show first name if available, otherwise fallback to athlete_name
  const getAthleteLabel = (athlete: AthleteData) => {
    // Try to extract first name from athlete.athlete_name (assume 'First Last'), or use athlete.first_name if present in data
    if ((athlete as any).first_name && (athlete as any).first_name.trim() !== '') {
      return (athlete as any).first_name;
    }
    if (athlete.athlete_name && athlete.athlete_name.trim() !== '') {
      return athlete.athlete_name.split(' ')[0];
    }
    return athlete.athlete_name;
  };

  // Helper function to reorder options: focused athlete first, then others
  const reorderOptions = (options: any[], focusedAthlete: string | null) => {
    if (!focusedAthlete) return options;

    const focusedOption = options.find(opt => opt.value === focusedAthlete);
    const otherOptions = options.filter(opt => opt.value !== focusedAthlete);

    return focusedOption ? [focusedOption, ...otherOptions] : options;
  };

  // Add CSS for scale animation
  const scaleTransition = 'transform 0.15s cubic-bezier(0.4,0,0.2,1)';

  // Themed styles for react-select using CSS variables
  const selectStyles = {
    control: (provided: any, state: any) => ({
      ...provided,
      backgroundColor: 'var(--bg-card)',
      borderColor: state.isFocused || state.menuIsOpen
        ? (isDark ? '#6b7280' : '#9ca3af')  // darker gray on focus/open
        : 'var(--card-border)',              // light gray default
      color: 'var(--text-primary)',
      borderRadius: '1rem',
      minHeight: '44px',
      boxShadow: 'none',
      transition: scaleTransition,
      transform: state.isFocused || state.menuIsOpen ? 'scale(1.03)' : 'scale(1)',
      '&:hover': {
        borderColor: isDark ? '#6b7280' : '#9ca3af',  // darker gray on hover
      },
    }),
    menu: (provided: any) => ({
      ...provided,
      backgroundColor: 'var(--bg-card)',
      borderRadius: '1rem',
      boxShadow: isDark ? '0 8px 32px 0 rgba(0,0,0,0.5)' : '0 8px 32px 0 rgba(0,0,0,0.1)',
      border: '1px solid var(--card-border)',
      marginTop: 6,
      zIndex: 30,
    }),
    option: (provided: any, state: any) => ({
      ...provided,
      backgroundColor: 'transparent',
      ':active': {
        ...provided[':active'],
        backgroundColor: 'transparent',
      },
      ':hover': {
        ...provided[':hover'],
        backgroundColor: 'var(--bg-button-hover)',
      },
      color: 'var(--text-primary)',
      borderRadius: '0.75rem',
      fontWeight: state.isSelected ? 700 : 500,
      fontSize: '1rem',
      padding: '10px 16px',
      cursor: 'pointer',
      transition: scaleTransition,
    }),
    multiValue: (provided: any) => ({
      ...provided,
      backgroundColor: 'var(--bg-button)',
      borderRadius: '0.75rem',
      color: 'var(--text-secondary)',
      fontWeight: 500,
      fontSize: '0.95rem',
      padding: '2px 6px',
    }),
    multiValueLabel: (provided: any) => ({
      ...provided,
      color: 'var(--text-secondary)',
      fontWeight: 500,
      fontSize: '0.95rem',
    }),
    multiValueRemove: (provided: any) => ({
      ...provided,
      color: 'var(--text-secondary)',
      ':hover': {
        backgroundColor: 'var(--accent-ui)',
        color: 'var(--bg-primary)',
      },
      borderRadius: '0.75rem',
    }),
    placeholder: (provided: any) => ({
      ...provided,
      color: 'var(--text-secondary)',
      fontWeight: 400,
      fontSize: '1rem',
    }),
    input: (provided: any) => ({
      ...provided,
      color: 'var(--text-secondary)',
      fontWeight: 500,
      fontSize: '1rem',
    }),
    dropdownIndicator: (provided: any, state: any) => ({
      ...provided,
      color: 'var(--text-secondary)',
      transition: 'color 0.2s',
      ':hover': {
        color: 'var(--text-primary)',
      },
    }),
    indicatorSeparator: (provided: any) => ({
      ...provided,
      backgroundColor: 'var(--card-border)',
    }),
    singleValue: (provided: any) => ({
      ...provided,
      color: 'var(--text-primary)',
      fontSize: '1rem',
    }),
  };

  // Custom Option component with checkbox and focus button
  const CheckboxOptionWithFocus = (metricType: 'recovery' | 'sleep' | 'strain' | 'hrZone' | 'zoneTrend' | 'rhr' | 'sleepStages' | 'dailyMileage' | 'weeklyMileage') => {
    return (props: any) => {
      const [hovered, setHovered] = React.useState(false);

      // Get the athlete name from the option value
      const athleteName = props.data.value;

      // Get current focused athlete for this metric
      const focusedAthlete = metricType === 'recovery' ? focusedRecoveryAthlete :
                            metricType === 'sleep' ? focusedSleepAthlete :
                            metricType === 'strain' ? focusedStrainAthlete :
                            metricType === 'hrZone' ? focusedHRZoneAthlete :
                            metricType === 'zoneTrend' ? focusedZoneTrendAthlete :
                            metricType === 'rhr' ? focusedRHRAthlete :
                            metricType === 'sleepStages' ? focusedSleepStagesAthlete :
                            metricType === 'dailyMileage' ? focusedDailyMileageAthlete :
                            focusedWeeklyMileageAthlete;
      const setFocusedAthlete = metricType === 'recovery' ? setFocusedRecoveryAthlete :
                                metricType === 'sleep' ? setFocusedSleepAthlete :
                                metricType === 'strain' ? setFocusedStrainAthlete :
                                metricType === 'hrZone' ? setFocusedHRZoneAthlete :
                                metricType === 'zoneTrend' ? setFocusedZoneTrendAthlete :
                                metricType === 'rhr' ? setFocusedRHRAthlete :
                                metricType === 'sleepStages' ? setFocusedSleepStagesAthlete :
                                metricType === 'dailyMileage' ? setFocusedDailyMileageAthlete :
                                setFocusedWeeklyMileageAthlete;

      const isFocused = focusedAthlete === athleteName;
      const isDimmed = focusedAthlete !== null && !isFocused;

      const handleFocusToggle = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setFocusedAthlete(isFocused ? null : athleteName);
      };

      return (
        <selectComponents.Option
          {...props}
          isDisabled={isDimmed}
          innerProps={{
            ...props.innerProps,
            onMouseEnter: (e: any) => {
              if (!isDimmed) setHovered(true);
              if (props.innerProps && props.innerProps.onMouseEnter) props.innerProps.onMouseEnter(e);
            },
            onMouseLeave: (e: any) => {
              setHovered(false);
              if (props.innerProps && props.innerProps.onMouseLeave) props.innerProps.onMouseLeave(e);
            },
            onClick: (e: any) => {
              // Prevent the option's onClick if we're clicking the focus button
              if (e.target.closest('button')) {
                e.preventDefault();
                e.stopPropagation();
                return;
              }
              // Otherwise, call the original onClick
              if (props.innerProps && props.innerProps.onClick) {
                props.innerProps.onClick(e);
              }
            },
          }}
        >
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
            opacity: isDimmed ? 0.4 : 1,
            pointerEvents: isDimmed ? 'none' : 'auto',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
              <input
                type="checkbox"
                checked={props.isSelected}
                onChange={() => { }}
                disabled={isDimmed}
                style={{
                  marginLeft: 2,
                  marginRight: 12,
                  width: 20,
                  height: 20,
                  transition: scaleTransition,
                  transform: hovered && !isDimmed ? 'scale(1.15)' : 'scale(1)',
                  transformOrigin: 'left center',
                  cursor: isDimmed ? 'not-allowed' : 'pointer',
                }}
              />
              <span className="cgm-text-primary" style={{
                textDecoration: isDimmed ? 'line-through' : 'none',
              }}>
                {props.label}
              </span>
            </div>
            {!isDimmed && props.isSelected && (
              <button
                onClick={handleFocusToggle}
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                className={isFocused ? '' : 'cgm-list-item'}
                style={{
                  marginLeft: 8,
                  padding: '6px',
                  borderRadius: '6px',
                  border: 'none',
                  backgroundColor: isFocused ? 'var(--accent-ui)' : undefined,
                  color: isFocused ? '#fff' : 'var(--text-secondary)',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                  <circle cx="12" cy="12" r="3"></circle>
                </svg>
              </button>
            )}
          </div>
        </selectComponents.Option>
      );
    };
  };

  // Keep the original CheckboxOption for non-analytics dropdowns
  const CheckboxOption = (props: any) => {
    const [hovered, setHovered] = React.useState(false);
    return (
      <selectComponents.Option
        {...props}
        innerProps={{
          ...props.innerProps,
          onMouseEnter: (e: any) => {
            setHovered(true);
            if (props.innerProps && props.innerProps.onMouseEnter) props.innerProps.onMouseEnter(e);
          },
          onMouseLeave: (e: any) => {
            setHovered(false);
            if (props.innerProps && props.innerProps.onMouseLeave) props.innerProps.onMouseLeave(e);
          },
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <input
            type="checkbox"
            checked={props.isSelected}
            onChange={() => { }}
            style={{
              marginLeft: 2,
              marginRight: 12,
              width: 20,
              height: 20,
              transition: scaleTransition,
              transform: hovered ? 'scale(1.15)' : 'scale(1)',
              transformOrigin: 'left center',
            }}
          />
          <span className="cgm-text-primary">{props.label}</span>
        </div>
      </selectComponents.Option>
    );
  };

  // Add a custom MultiValue component that hides the selected tags
  const HiddenMultiValue = () => null;

  // Custom Menu component with Select All functionality
  const MenuWithSelectAll = (metricType: 'recovery' | 'sleep' | 'strain' | 'hrZone' | 'zoneTrend' | 'rhr' | 'sleepStages' | 'dailyMileage' | 'weeklyMileage') => {
    return (props: any) => {
      const allAthletes = props.options.map((opt: any) => opt.value);
      const selectedAthletes = metricType === 'recovery' ? selectedRecoveryAthletes :
                              metricType === 'sleep' ? selectedSleepAthletes :
                              metricType === 'strain' ? selectedStrainAthletes :
                              metricType === 'hrZone' ? selectedHRZoneAthletes :
                              metricType === 'zoneTrend' ? selectedZoneTrendAthletes :
                              metricType === 'rhr' ? selectedRHRAthletes :
                              metricType === 'sleepStages' ? selectedSleepStagesAthletes :
                              metricType === 'dailyMileage' ? selectedDailyMileageAthletes :
                              selectedWeeklyMileageAthletes;
      const setSelectedAthletes = metricType === 'recovery' ? setSelectedRecoveryAthletes :
                                  metricType === 'sleep' ? setSelectedSleepAthletes :
                                  metricType === 'strain' ? setSelectedStrainAthletes :
                                  metricType === 'hrZone' ? setSelectedHRZoneAthletes :
                                  metricType === 'zoneTrend' ? setSelectedZoneTrendAthletes :
                                  metricType === 'rhr' ? setSelectedRHRAthletes :
                                  metricType === 'sleepStages' ? setSelectedSleepStagesAthletes :
                                  metricType === 'dailyMileage' ? setSelectedDailyMileageAthletes :
                                  setSelectedWeeklyMileageAthletes;
      const allSelected = selectedAthletes.length === allAthletes.length;

      const handleSelectAll = () => {
        if (allSelected) {
          setSelectedAthletes([]);
        } else {
          setSelectedAthletes(allAthletes);
        }
      };

      return (
        <selectComponents.Menu {...props}>
          <div
            onClick={handleSelectAll}
            className="cgm-list-item"
            style={{
              padding: '10px 12px',
              cursor: 'pointer',
              borderTop: '1px solid var(--card-border)',
              borderBottom: '1px solid var(--card-border)',
              borderTopLeftRadius: '12px',
              borderTopRightRadius: '12px',
              fontWeight: 600,
              fontSize: '0.875rem',
              color: 'var(--text-secondary)',
              transition: 'background-color 0.2s',
            }}
          >
            {allSelected ? '− Deselect All' : '+ Select All'}
          </div>
          <div>{props.children}</div>
        </selectComponents.Menu>
      );
    };
  };

  // Custom ValueContainer for the main Athletes dropdown
  const AthletesValueContainer = (props: any) => {
    return (
      <selectComponents.ValueContainer {...props}>
        {props.children}
      </selectComponents.ValueContainer>
    );
  };

  // ValueContainers for individual Analytics charts
  const RecoveryValueContainer = (props: any) => {
    return (
      <selectComponents.ValueContainer {...props}>
        {props.children}
      </selectComponents.ValueContainer>
    );
  };

  const SleepValueContainer = (props: any) => {
    return (
      <selectComponents.ValueContainer {...props}>
        {props.children}
      </selectComponents.ValueContainer>
    );
  };

  const StrainValueContainer = (props: any) => {
    const total = selectedTeam?.activity_data?.athletes_data.length || selectedTeam?.recovery_data?.athletes_data.length || 0;
    const selected = selectedStrainAthletes.length;
    return (
      <selectComponents.ValueContainer {...props}>
        {!props.selectProps.inputValue}
        {props.children}
      </selectComponents.ValueContainer>
    );
  };

  // ValueContainers for new filters (HR Zones, Zone Trends, RHR, Sleep Stages)
  const HRZoneValueContainer = (props: any) => {
    return (
      <selectComponents.ValueContainer {...props}>
        {props.children}
      </selectComponents.ValueContainer>
    );
  };

  const ZoneTrendValueContainer = (props: any) => {
    return (
      <selectComponents.ValueContainer {...props}>
        {!props.selectProps.inputValue}
        {props.children}
      </selectComponents.ValueContainer>
    );
  };

  const RHRValueContainer = (props: any) => {
    return (
      <selectComponents.ValueContainer {...props}>
        {props.children}
      </selectComponents.ValueContainer>
    );
  };

  const SleepStagesValueContainer = (props: any) => {
    return (
      <selectComponents.ValueContainer {...props}>
        {!props.selectProps.inputValue}
        {props.children}
      </selectComponents.ValueContainer>
    );
  };

  const DailyMileageValueContainer = (props: any) => {
    return (
      <selectComponents.ValueContainer {...props}>
        {props.children}
      </selectComponents.ValueContainer>
    );
  };

  const WeeklyMileageValueContainer = (props: any) => {
    return (
      <selectComponents.ValueContainer {...props}>
        {props.children}
      </selectComponents.ValueContainer>
    );
  };

  return (
    <ChatSessionProvider>
    <div className="min-h-screen cgm-bg-primary">
      {/* Enhanced Navigation */}
      <Header
        userInfo={userInfo}
        isDark={isDark}
        commands={commands}
      />

      {/* Main Content */}
      <DashboardLayout>
        {/* Team Selection & Controls */}
        <div className="mb-8">
          <div className={`cgm-card rounded-2xl p-6 border ${isDark ? 'border-gray-700/50' : 'border-gray-200/50'} shadow-xl`}>
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
              <div className="space-y-3">
                <div className="flex items-center gap-4">
                  <h2 className="text-2xl font-bold cgm-text-primary">Team Analytics</h2>

                  {/* Manual Refresh Button */}
                  <button
                    onClick={handleManualRefresh}
                    disabled={isRefreshing}
                    className={`cgm-button flex items-center gap-2 px-4 py-2 rounded-xl font-medium transition-all duration-200 transform hover:scale-105 ${
                      isRefreshing ? 'opacity-60 cursor-not-allowed' : ''
                    }`}
                    title={isRefreshing ? 'Refreshing...' : 'Refresh data'}
                  >
                    <svg
                      className={`w-5 h-5 ${isRefreshing ? 'animate-spin' : ''}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                      />
                    </svg>
                    {!isRefreshing && <span className="hidden sm:inline">Refresh</span>}
                  </button>

                  {/* Auto-refresh status indicator */}
                  {lastRefresh && (
                    <div className="text-xs cgm-text-secondary flex items-center gap-1">
                      <span className={`w-2 h-2 rounded-full ${autoRefresh ? 'bg-green-500 animate-pulse' : 'bg-gray-400'}`}></span>
                      <span className="hidden lg:inline">
                        Last updated: {lastRefresh.toLocaleTimeString()}
                      </span>
                    </div>
                  )}
                </div>
                <p className="cgm-text-secondary">Monitor your team's health and performance metrics</p>
                {/* Terra Teams Coach Connection removed - not implementing Catapult yet */}
              </div>
              <div className="flex flex-col sm:flex-row gap-4 lg:min-w-[400px]">
                <div className="flex-1" ref={teamRef} data-tour="coach-team-selector">
                  <label className="block text-sm font-medium cgm-text-primary mb-2">Team</label>
                  <Select
                    isMulti={false}
                    options={teamsData.map(team => ({ value: team.id, label: team.name }))}
                    value={selectedTeam ? { value: selectedTeam.id, label: selectedTeam.name } : null}
                    onChange={opt => {
                      if (!opt) return;
                      const team = teamsData.find(t => t.id === opt.value);
                      if (team) setSelectedTeam(team);
                    }}
                    classNamePrefix="react-select"
                    placeholder="Select a team"
                    menuPortalTarget={typeof window !== 'undefined' ? window.document.body : undefined}
                    menuIsOpen={teamMenuOpen}
                    onMenuOpen={() => setTeamMenuOpen(true)}
                    onMenuClose={() => setTeamMenuOpen(false)}
                    styles={{
                      ...selectStyles,
                      control: (provided: any, state: any) => ({
                        ...selectStyles.control(provided, state),
                        minWidth: 0,
                        maxWidth: '100%',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }),
                      option: (provided: any, state: any) => ({
                        ...selectStyles.option(provided, state),
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        display: 'flex',
                        alignItems: 'center',
                      }),
                      menu: (provided: any) => ({
                        ...provided,
                        ...(selectStyles.menu ? selectStyles.menu(provided) : {}),
                        zIndex: 9999,
                      }),
                      menuPortal: (base: any) => ({
                        ...base,
                        zIndex: 99999,
                      }),
                    }}
                  />
                </div>

                <div className="flex-1" ref={periodRef}>
                  <label className="block text-sm font-medium cgm-text-primary mb-2">Period</label>
                  <Select
                    isMulti={false}
                    options={[
                      { value: 7, label: 'Last 7 days' },
                      { value: 14, label: 'Last 2 weeks' },
                      { value: 30, label: 'Last 30 days' },
                      { value: 60, label: 'Last 60 days' },
                      { value: 90, label: 'Last 90 days' },
                    ]}
                    value={{ value: days, label: `Last ${days === 7 ? '7 days' : days === 14 ? '2 weeks' : days + ' days'}` }}
                    onChange={opt => {
                      if (!opt) return;
                      setDays(opt.value);
                    }}

                    classNamePrefix="react-select"
                    placeholder="Select period"
                    menuPortalTarget={typeof window !== 'undefined' ? window.document.body : undefined}
                    menuIsOpen={periodMenuOpen}
                    onMenuOpen={() => setPeriodMenuOpen(true)}
                    onMenuClose={() => setPeriodMenuOpen(false)}
                    styles={{
                      ...selectStyles,
                      control: (provided: any, state: any) => ({
                        ...selectStyles.control(provided, state),
                        minWidth: 0,
                        maxWidth: '100%',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }),
                      option: (provided: any, state: any) => ({
                        ...selectStyles.option(provided, state),
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        display: 'flex',
                        alignItems: 'center',
                      }),
                      menu: (provided: any) => ({
                        ...provided,
                        ...(selectStyles.menu ? selectStyles.menu(provided) : {}),
                        zIndex: 9999,
                      }),
                      menuPortal: (base: any) => ({
                        ...base,
                        zIndex: 99999,
                      }),
                    }}
                  />
                </div>


              </div>
            </div>
          </div>
        </div>

        {selectedTeam && (
          <>
            {/* Navigation Tabs */}
            <TabNavigation
              tabs={[
                { id: 'overview', label: 'Team Overview', icon: 'LayoutDashboard' },
                { id: 'analytics', label: 'Analytics Dashboard', icon: 'TrendingUp' },
                { id: 'rankings', label: 'Rankings', icon: 'Trophy' },
                { id: 'athletes', label: 'Athletes', icon: 'Users' },
                { id: 'chat', label: 'AI Chat', icon: 'Bot' }
              ]}
              defaultTab="overview"
              paramName="view"
              dataTourPrefix="coach-tab"
            />

            {/* Content Sections */}
            {activeView === 'overview' && (() => {
              const isCoachOrAdmin = userInfo?.role === 'coach' || userInfo?.role === 'admin';
              const athleteCount = selectedTeam?.athletes?.length ?? 0;
              const showOnboarding =
                isCoachOrAdmin &&
                athleteCount === 0 &&
                selectedTeam?.id != null &&
                !onboardingDismissedTeams.has(selectedTeam.id);
              if (showOnboarding) {
                return (
                  <CoachOnboardingPanel
                    selectedTeam={selectedTeam}
                    onInviteAthletes={() => openInviteUserModal({ defaultRole: 'athlete' })}
                    onInviteCoaches={() => openInviteUserModal({ defaultRole: 'coach' })}
                    onDismiss={() => dismissOnboardingForTeam(selectedTeam.id)}
                  />
                );
              }
              return (
                <TeamOverviewTab
                  selectedTeam={selectedTeam}
                  teamInsights={teamInsights}
                  rankings={rankings}
                  days={days}
                  isDark={isDark}
                  handleAthleteDoubleClick={handleAthleteDoubleClick}
                  onAddUser={isCoachOrAdmin ? openInviteUserModal : undefined}
                  userRole={userInfo?.role}
                  onRiskMapReady={handleRiskMapReady}
                />
              );
            })()}

            {activeView === 'analytics' && (
              <>
                <DataSourceFilter
                  selectedSources={selectedDataSources}
                  connectedSources={teamConnectedSources}
                  onSourceChange={(sources) => {
                    setSelectedDataSources(sources);
                    // Flag to prevent the selectedTeam effect from pruning athlete selections
                    // while the refetch is in flight. The data-source-specific effect handles
                    // re-initializing athlete selections when sources are re-enabled.
                    // Note: we intentionally keep garmin_data intact so the data-source effect
                    // can read it for re-initialization; charts already gate on selectedDataSources.
                    if (selectedTeam) {
                      isSourceToggleRef.current = true;
                    }
                    // Trigger data refresh when source changes - pass sources directly to avoid stale closure
                    fetchTeamsData(true, sources);
                  }}
                />
                <AnalyticsDashboardTab
                  selectedTeam={selectedTeam}
                  isDark={isDark}
                  days={days}
                  athleteColorMap={athleteColorMap}
                  selectedRecoveryAthletes={selectedRecoveryAthletes}
                  selectedSleepAthletes={selectedSleepAthletes}
                  selectedStrainAthletes={selectedStrainAthletes}
                  setSelectedRecoveryAthletes={setSelectedRecoveryAthletes}
                  setSelectedSleepAthletes={setSelectedSleepAthletes}
                  setSelectedStrainAthletes={setSelectedStrainAthletes}
                  recoveryMenuOpen={recoveryMenuOpen}
                  sleepMenuOpen={sleepMenuOpen}
                  strainMenuOpen={strainMenuOpen}
                  setRecoveryMenuOpen={setRecoveryMenuOpen}
                  setSleepMenuOpen={setSleepMenuOpen}
                  setStrainMenuOpen={setStrainMenuOpen}
                  generateFilteredRecoveryChartData={generateFilteredRecoveryChartData}
                  generateFilteredSleepChartData={generateFilteredSleepChartData}
                  generateFilteredActivityChartData={generateFilteredActivityChartData}
                  getMetricChartOptions={getMetricChartOptions}
                  getSleepChartOptions={getSleepChartOptions}
                  getAthleteLabel={getAthleteLabel}
                  selectStyles={selectStyles}
                  CheckboxOption={CheckboxOption}
                  HiddenMultiValue={HiddenMultiValue}
                  RecoveryValueContainer={RecoveryValueContainer}
                  SleepValueContainer={SleepValueContainer}
                  StrainValueContainer={StrainValueContainer}
                  RecoveryMenuWithSelectAll={MenuWithSelectAll('recovery')}
                  SleepMenuWithSelectAll={MenuWithSelectAll('sleep')}
                  StrainMenuWithSelectAll={MenuWithSelectAll('strain')}
                  RecoveryCheckboxOption={CheckboxOptionWithFocus('recovery')}
                  SleepCheckboxOption={CheckboxOptionWithFocus('sleep')}
                  StrainCheckboxOption={CheckboxOptionWithFocus('strain')}
                  focusedRecoveryAthlete={focusedRecoveryAthlete}
                  focusedSleepAthlete={focusedSleepAthlete}
                  focusedStrainAthlete={focusedStrainAthlete}
                  reorderOptions={reorderOptions}
                  selectedDataSources={selectedDataSources}
                  // New filter props for HR Zones, Zone Trends, RHR, Sleep Stages
                  selectedHRZoneAthletes={selectedHRZoneAthletes}
                  setSelectedHRZoneAthletes={setSelectedHRZoneAthletes}
                  hrZoneMenuOpen={hrZoneMenuOpen}
                  setHRZoneMenuOpen={setHRZoneMenuOpen}
                  HRZoneValueContainer={HRZoneValueContainer}
                  HRZoneMenuWithSelectAll={MenuWithSelectAll('hrZone')}
                  HRZoneCheckboxOption={CheckboxOptionWithFocus('hrZone')}
                  focusedHRZoneAthlete={focusedHRZoneAthlete}
                  selectedZoneTrendAthletes={selectedZoneTrendAthletes}
                  setSelectedZoneTrendAthletes={setSelectedZoneTrendAthletes}
                  zoneTrendMenuOpen={zoneTrendMenuOpen}
                  setZoneTrendMenuOpen={setZoneTrendMenuOpen}
                  ZoneTrendValueContainer={ZoneTrendValueContainer}
                  ZoneTrendMenuWithSelectAll={MenuWithSelectAll('zoneTrend')}
                  ZoneTrendCheckboxOption={CheckboxOptionWithFocus('zoneTrend')}
                  focusedZoneTrendAthlete={focusedZoneTrendAthlete}
                  selectedRHRAthletes={selectedRHRAthletes}
                  setSelectedRHRAthletes={setSelectedRHRAthletes}
                  rhrMenuOpen={rhrMenuOpen}
                  setRHRMenuOpen={setRHRMenuOpen}
                  RHRValueContainer={RHRValueContainer}
                  RHRMenuWithSelectAll={MenuWithSelectAll('rhr')}
                  RHRCheckboxOption={CheckboxOptionWithFocus('rhr')}
                  focusedRHRAthlete={focusedRHRAthlete}
                  setFocusedRHRAthlete={setFocusedRHRAthlete}
                  selectedSleepStagesAthletes={selectedSleepStagesAthletes}
                  setSelectedSleepStagesAthletes={setSelectedSleepStagesAthletes}
                  sleepStagesMenuOpen={sleepStagesMenuOpen}
                  setSleepStagesMenuOpen={setSleepStagesMenuOpen}
                  SleepStagesValueContainer={SleepStagesValueContainer}
                  SleepStagesMenuWithSelectAll={MenuWithSelectAll('sleepStages')}
                  SleepStagesCheckboxOption={CheckboxOptionWithFocus('sleepStages')}
                  focusedSleepStagesAthlete={focusedSleepStagesAthlete}
                  // Daily Mileage filter props
                  selectedDailyMileageAthletes={selectedDailyMileageAthletes}
                  setSelectedDailyMileageAthletes={setSelectedDailyMileageAthletes}
                  dailyMileageMenuOpen={dailyMileageMenuOpen}
                  setDailyMileageMenuOpen={setDailyMileageMenuOpen}
                  DailyMileageValueContainer={DailyMileageValueContainer}
                  DailyMileageMenuWithSelectAll={MenuWithSelectAll('dailyMileage')}
                  DailyMileageCheckboxOption={CheckboxOptionWithFocus('dailyMileage')}
                  focusedDailyMileageAthlete={focusedDailyMileageAthlete}
                  setFocusedDailyMileageAthlete={setFocusedDailyMileageAthlete}
                  // Weekly Mileage filter props
                  selectedWeeklyMileageAthletes={selectedWeeklyMileageAthletes}
                  setSelectedWeeklyMileageAthletes={setSelectedWeeklyMileageAthletes}
                  weeklyMileageMenuOpen={weeklyMileageMenuOpen}
                  setWeeklyMileageMenuOpen={setWeeklyMileageMenuOpen}
                  WeeklyMileageValueContainer={WeeklyMileageValueContainer}
                  WeeklyMileageMenuWithSelectAll={MenuWithSelectAll('weeklyMileage')}
                  WeeklyMileageCheckboxOption={CheckboxOptionWithFocus('weeklyMileage')}
                  focusedWeeklyMileageAthlete={focusedWeeklyMileageAthlete}
                  setFocusedWeeklyMileageAthlete={setFocusedWeeklyMileageAthlete}
                />
              </>
            )}

            {activeView === 'rankings' && (
              <RankingsTab
                selectedTeam={selectedTeam}
                isDark={isDark}
                RankingTable={RankingTable}
              />
            )}

            {activeView === 'athletes' && (
              <AthletesTab
                selectedTeam={selectedTeam}
                isDark={isDark}
                handleAthleteDoubleClick={handleAthleteDoubleClick}
                onAddAthlete={userInfo?.role === 'coach' || userInfo?.role === 'admin' ? openInviteUserModal : undefined}
                userRole={userInfo?.role}
                riskScoreMap={riskScoreMap}
              />
            )}

            {activeView === 'chat' && selectedTeam && (
              <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
                {/* Main Chat Area - 2/3 width on large screens */}
                <div className="xl:col-span-2">
                  <CoachChat selectedTeam={selectedTeam} days={days} />
                </div>

                {/* Knowledge Base Sidebar - 1/3 width on large screens */}
                <div className="xl:col-span-1">
                  <DocumentUpload
                    teams={teamsData}
                    selectedTeam={selectedTeam.id}
                    onTeamChange={(teamId) => {
                      const newTeam = teamsData.find(t => t.id === teamId);
                      if (newTeam) {
                        setSelectedTeam(newTeam);
                      }
                    }}
                  />
                </div>
              </div>
            )}
          </>
        )}
      </DashboardLayout>

    </div>
    </ChatSessionProvider>
  );
};

export default CoachDashboard;
